/**
 * ClientTransportHandler.ts
 * Client-side Communication Manager
 * 
 * Singleton that manages communication to the Control Bridge.
 * Supports both WebSocket (web) and iOS WebKit bridge (native app).
 * Wraps commands in BATON envelopes and parses incoming events.
 * 
 * Usage:
 *   const client = getClientTransport();
 *   await client.connect('ws://localhost:3001');
 *   client.onPlaybackStateChanged((state) => console.log(state));
 *   client.sendCommand({ cmd: 'play', streamUrl: '...' });
 */

import {
  BatonMessage,
  CommandPayload,
  EventPayload,
  ResponsePayload,
  HandlerId,
  HandlerLocation,
  PlaybackState,
  TrackMetadata,
  ConnectionState,
  validateMessage,
  createCommand,
  PROTOCOL_VERSION,
  isSuccess,
} from '../protocols/BATON';
import { rlog } from '../utils/remoteLogger';
import { detectPlatform } from '../utils/platform';

import { useMaestroStore } from '../store/maestroStore';
import { getClientViewHandler } from './ClientViewHandler';
import { getMetadataHandler } from './MetadataHandler';
import { getClientDiscoveryHandler } from './ClientDiscoveryHandler';
import { getClientMultizoneHandler } from './ClientMultizoneHandler';
import { getSyncHandler } from './ClientSyncHandler';
import { CLOUD_API_URL, getWebSocketUrl } from '../config/serverConfig';
import {
  BATON_RECONNECT_POLICY,
  computeReconnectDelayMs,
  isAuthWebSocketClose,
  shouldProbeAuthAfterWsFailures,
} from '../lib/batonReconnectPolicy';
import { canAttemptBatonConnect } from '../lib/batonAuthGate';
import { isBridgeAvailable, getBridgeMode } from '../lib/playback-bridge';
import { TransportCommand } from '../transport/TransportCommand';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { recordBatonWebSocketDiagnostic } from '../lib/batonReconnectSessionTracker';
import { clearExclusiveErrorOnPlaybackConfirmed } from '../utils/exclusiveFallbackUI'; import { shouldRouteStickyShared } from '../utils/stickySharedFallback';

// =============================================================================
// STREAM URL REFRESH FLAG — MODULE-LEVEL
// =============================================================================
// After a BATON handshake the server pushes the last-known track state including
// a Qobuz stream URL that was generated for a DIFFERENT device and has since
// expired. Instead of trying to block that state (sync windows — fragile, many
// leak paths), we let it flow through normally and simply mark that the first
// play after connect must re-resolve the stream URL from Qobuz before loading
// it into the audio element.

let _requiresStreamUrlRefresh = false;

export function requiresStreamUrlRefresh(): boolean {
  return _requiresStreamUrlRefresh;
}

export function clearStreamUrlRefreshRequired(): void {
  _requiresStreamUrlRefresh = false;
}

// =============================================================================
// iOS WEBKIT BRIDGE TYPE DECLARATIONS
// =============================================================================

/**
 * iOS WKWebView message handler interface
 * This is the standard way iOS apps receive messages from JavaScript
 */
interface WebKitMessageHandler {
  postMessage(message: string): void;
}

interface WebKitMessageHandlers {
  baton?: WebKitMessageHandler;
}

interface WebKit {
  messageHandlers: WebKitMessageHandlers;
}

declare global {
  interface Window {
    webkit?: WebKit;
  }
}

// =============================================================================
// DEVICE IDENTIFICATION
// =============================================================================

/**
 * Get or generate a persistent device ID
 * Used for both BATON and UI sync to identify this device
 */
function getDeviceId(): string {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('maestro_device_id');
    if (stored) return stored;
    
    const prefix = detectPlatform().deviceIdPrefix;
    const id = `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem('maestro_device_id', id);
    return id;
  }
  return `temp_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 9)}`;
}

function getDeviceType(): string {
  return detectPlatform().deviceType;
}

/**
 * Get user ID for BATON room grouping from the stored auth session.
 * Falls back to 'default' only when no auth data is available.
 */
function getUserId(): string {
  if (typeof localStorage === 'undefined') return 'default';
  try {
    const stored = localStorage.getItem('maestro_auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      const uid = parsed?.user?.id;
      if (uid && typeof uid === 'string' && uid.length > 0) return uid;
    }
  } catch { /* ignore parse errors */ }
  return 'default';
}

// =============================================================================
// TYPES
// =============================================================================

/** Transport mode */
export type TransportMode = 'websocket' | 'native' | 'none';

/** Connection status */
export type ClientConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Why BATON reconnect was halted — gates online/manual recovery. */
export type ReconnectHaltReason = 'none' | 'auth' | 'exhausted' | 'explicit';

/** Callback types for event subscriptions */
export type PlaybackStateCallback = (state: PlaybackState) => void;
export type PositionCallback = (position: number, duration: number) => void;
export type TrackCallback = (track: TrackMetadata | null) => void;
export type VolumeCallback = (level: number, muted: boolean) => void;
export type ConnectionCallback = (state: ClientConnectionState) => void;
export type EventCallback = (event: BatonMessage<EventPayload>) => void;

/** Pending command awaiting response */
interface PendingCommand {
  resolve: (response: BatonMessage<ResponsePayload>) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

// =============================================================================
// CLIENT TRANSPORT HANDLER
// =============================================================================

export class ClientTransportHandler {
  private ws: WebSocket | null = null;
  private connectionState: ClientConnectionState = 'disconnected';
  private serverUrl: string = '';
  private reconnectAttempts = 0;
  private maxReconnectAttempts = BATON_RECONNECT_POLICY.MAX_ATTEMPTS_PER_CYCLE;
  private reconnectDelay = BATON_RECONNECT_POLICY.BASE_DELAY_MS;
  private reconnectCooldownCycles = 0;
  private maxReconnectCooldownCycles = BATON_RECONNECT_POLICY.MAX_COOLDOWN_CYCLES;
  private commandTimeout = 10000; // 10 seconds
  private reconnectCooldownTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectInFlight = false;
  private reconnectHalted = false;
  private reconnectHaltReason: ReconnectHaltReason = 'none';
  private keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  /** Consecutive WS connect failures since the last successful onopen this session. */
  private consecutiveConnectFailuresSinceSuccess = 0;
  private authProbeInFlight = false;
  private networkOffline = false;
  
  // Transport mode (websocket or native iOS bridge)
  private transportMode: TransportMode = 'none';
  
  // Pending commands waiting for response
  private pendingCommands: Map<string, PendingCommand> = new Map();
  
  // Event subscribers
  private playbackStateCallbacks: Set<PlaybackStateCallback> = new Set();
  private positionCallbacks: Set<PositionCallback> = new Set();
  private trackCallbacks: Set<TrackCallback> = new Set();
  private volumeCallbacks: Set<VolumeCallback> = new Set();
  private connectionCallbacks: Set<ConnectionCallback> = new Set();
  private genericEventCallbacks: Set<EventCallback> = new Set();
  
  // Handler ready state
  private isHandlerReady = false;

  // Set true before the init command; consumed in handleResponse() to mark
  // that the next play requires a fresh stream URL (stale Qobuz URL guard).
  private _awaitingHandshakeResponse = false;
  
  // Native message listener reference (for cleanup)
  private nativeMessageListener: ((event: MessageEvent) => void) | null = null;
  
  // ---------------------------------------------------------------------------
  // BROWSER AS BATON DEVICE: Audio element reference & position broadcasting
  // ---------------------------------------------------------------------------
  private audioElement: HTMLAudioElement | null = null;
  private positionBroadcastInterval: ReturnType<typeof setInterval> | null = null;
  private lastBroadcastPosition: number = -1; // Throttle: only send if changed
  
  // ---------------------------------------------------------------------------
  // POSITION LOCK: Prevents server position updates during user scrubbing
  // ---------------------------------------------------------------------------
  // When user is dragging the scrubber, we don't want server position updates
  // to "snap back" the scrubber. App.jsx sets this lock while dragging.
  private positionLocked: boolean = false;

  // Play-protect window: timestamp of last play_uri sent to a connector renderer.
  // pause() uses this to block phantom pauses during the 2s after play starts.
  private _lastConnectorPlaySentAt = 0;
  private _lastBridgePlayedTrackId: string | null = null;
  
  // ---------------------------------------------------------------------------
  // CONSTRUCTOR - Sets up global bridge function immediately
  // ---------------------------------------------------------------------------
  
  constructor() {
    // Expose global bridge function for Swift to call
    // This MUST be available before connect() so Swift can send events immediately
    this.setupGlobalBridgeFunction();
    console.log('[ClientTransport] 📲 Global bridge function ready: window.receiveNativeBatonEvent()');
    console.log('[ClientTransport] 🔍 Checking global function exists:', typeof (window as any).receiveNativeBatonEvent);
    
    const _plat = detectPlatform();
    if (_plat.isIOSNativeApp) {
      console.log('[ClientTransport] Running on iOS native app:', _plat.deviceName);
    } else if (_plat.isTauriDesktop) {
      console.log('[ClientTransport] Running on Tauri desktop');
    } else {
      console.log('[ClientTransport] 🌐 Running on web - no native bridge');
    }
  }
  
  /**
   * Set up the global function that Swift can call to send events to JavaScript
   * Swift calls: webView.evaluateJavaScript("receiveNativeBatonEvent('\(jsonString)')")
   */
  private setupGlobalBridgeFunction(): void {
    // Primary function - exact name Swift will call
    (window as any).receiveNativeBatonEvent = (jsonString: string) => {
      console.log('📥 [ClientTransport] ====== NATIVE EVENT RECEIVED ======');
      console.log('📥 [ClientTransport] Raw JSON:', jsonString?.substring(0, 300));
      try {
        const event = JSON.parse(jsonString);
        console.log('📥 [ClientTransport] Parsed event type:', event?.type);
        console.log('📥 [ClientTransport] Parsed event handler:', event?.handler);
        // Feed this event into the standard handler logic
        this.handleIncomingMessage(event);
      } catch (err) {
        console.error('❌ [ClientTransport] Failed to parse native event:', err);
        console.error('❌ [ClientTransport] Raw string was:', jsonString);
      }
    };
    
    // Alias for backwards compatibility
    (window as any).batonReceiveEvent = (window as any).receiveNativeBatonEvent;
  }
  
  /**
   * Local-core emits type-only JSON (no BATON envelope).
   * Known types are normalized into synthetic BATON events for existing listeners.
   */
  private static readonly TYPE_ONLY_LOCAL_EVENTS = new Set(['PEERS_UPDATED']);

  private isTypeOnlyLocalCoreMessage(raw: unknown): raw is { type: string } {
    if (typeof raw !== 'object' || raw === null) return false;
    const m = raw as Record<string, unknown>;
    if (typeof m.type !== 'string') return false;
    if (!ClientTransportHandler.TYPE_ONLY_LOCAL_EVENTS.has(m.type)) return false;
    const keys = Object.keys(m);
    return keys.length === 1 && keys[0] === 'type';
  }

  private handleTypeOnlyLocalCoreMessage(raw: { type: string }): void {
    if (raw.type === 'PEERS_UPDATED') {
      const synthetic: BatonMessage<EventPayload> = {
        v: PROTOCOL_VERSION,
        id: `local-peers-${Date.now()}`,
        ts: Date.now(),
        type: 'event',
        handler: 'peers',
        payload: { event: 'PEERS_UPDATED' },
      };
      this.genericEventCallbacks.forEach((cb) => cb(synthetic));
    }
  }

  /**
   * PUBLIC: Handle an incoming BATON message (from any source)
   * Can be called from:
   * - WebSocket onmessage
   * - iOS native bridge via receiveNativeBatonEvent()
   * - window.postMessage listener
   *
   * BROWSER AS BATON DEVICE: Now also handles incoming COMMANDS from server
   * (e.g., setVolume from iPhone remote → server → browser)
   */
  public handleIncomingMessage(message: BatonMessage | string): void {
    // If string, parse it first
    if (typeof message === 'string') {
      this.handleMessage(message);
    } else {
      if (this.isTypeOnlyLocalCoreMessage(message)) {
        this.handleTypeOnlyLocalCoreMessage(message);
        return;
      }
      // Already parsed, validate and route
      if (!validateMessage(message)) {
        console.warn('[ClientTransport] Invalid message format:', message);
        return;
      }
      
      const batonMessage = message as BatonMessage;
      
      switch (batonMessage.type) {
        case 'response':
          this.handleResponse(batonMessage as BatonMessage<ResponsePayload>);
          break;
        
        case 'event':
          this.handleEvent(batonMessage as BatonMessage<EventPayload>);
          break;
        
        case 'command':
          // BROWSER AS BATON DEVICE: Handle remote commands from server
          this.handleRemoteCommand(batonMessage as BatonMessage<CommandPayload>);
          break;
      }
    }
  }
  
  /**
   * Handle incoming command from server (Browser as BATON Device)
   * 
   * When iPhone sends setVolume to server, server may forward it to browser.
   * Browser must execute the command locally (update store + audio element).
   */
  private handleRemoteCommand(message: BatonMessage<CommandPayload>): void {
    const payload = message.payload as any;
    const cmd = payload?.cmd;
    
    console.log(`[ClientTransport] 📥 Remote command received: ${cmd}`);

    if (this.isConnectorRendererActive()) {
      console.log(`[ClientTransport] 🛡️ Connector owns renderer — skipping remote '${cmd}' command`);
      return;
    }

    rlog('CTH', 'remote_cmd', { cmd });
    
    switch (cmd) {
      case 'setVolume': {
        // Extract and sanitize level (0-100 integer)
        const rawLevel = payload.level;
        const level = Math.max(0, Math.min(100, Math.floor(Number(rawLevel) || 0)));
        
        console.log(`[ClientTransport] 🔊 Remote setVolume: ${level}%`);
        
        // 1. Update Zustand store (UI state)
        const store = useMaestroStore.getState();
        store.setVolume(level);
        store.setMuted(false);
        
        // 2. Apply to HTML5 audio element (actual audio)
        this.applyVolumeToAudioElement(level);
        
        // 3. Notify callbacks (legacy listeners)
        this.volumeCallbacks.forEach((cb) => cb(level, false));
        break;
      }
      
      case 'mute': {
        console.log('[ClientTransport] 🔇 Remote mute command');
        const store = useMaestroStore.getState();
        store.setMuted(true);
        
        if (this.audioElement) {
          this.audioElement.muted = true;
        }
        
        this.volumeCallbacks.forEach((cb) => cb(store.volume, true));
        break;
      }
      
      case 'unmute': {
        console.log('[ClientTransport] 🔊 Remote unmute command');
        const store = useMaestroStore.getState();
        store.setMuted(false);
        
        if (this.audioElement) {
          this.audioElement.muted = false;
        }
        
        this.volumeCallbacks.forEach((cb) => cb(store.volume, false));
        break;
      }
      
      // -----------------------------------------------------------------------
      // PLAY/PAUSE COMMANDS (Browser as Renderer)
      // When server forwards play/pause from another client, browser should act
      // -----------------------------------------------------------------------
      case 'play': {
        console.log('[ClientTransport] ▶️ Remote play command received');
        
        const storePlay = useMaestroStore.getState();
        const zonePlay = storePlay.system?.zoneName || '';
        
        // Update UI state + notify WebAudioRenderer via callbacks
        storePlay.setPlaybackState('playing');
        this.playbackStateCallbacks.forEach((cb) => cb('playing'));
        
        // TransportCommand owns all playback commands (play/pause/seek/volume).
        // Do NOT forward to iOS native bridge here — causes dual-play.
        // WebAudioRenderer handles local audio.play() via playbackStateCallbacks above.
        break;
      }
      
      case 'pause': {
        console.log('[ClientTransport] ⏸️ Remote pause command received');
        
        const storePause = useMaestroStore.getState();
        const zonePause = storePause.system?.zoneName || '';
        
        // Update UI state + notify WebAudioRenderer via callbacks
        storePause.setPlaybackState('paused');
        this.playbackStateCallbacks.forEach((cb) => cb('paused'));
        
        // TransportCommand owns all playback commands (play/pause/seek/volume).
        // Do NOT forward to iOS native bridge here — causes dual-play.
        // WebAudioRenderer handles local audio.pause() via playbackStateCallbacks above.
        break;
      }
      
      // -----------------------------------------------------------------------
      // SEEK COMMAND (Browser as Renderer)
      // When iPhone sends seek via Cloud, browser MUST jump to that position
      // This executes regardless of message source (cloud, local, bridge)
      // -----------------------------------------------------------------------
      case 'seek': {
        const newPos = Math.max(0, Math.floor(Number(payload.position) || 0));
        
        console.log(`[ClientTransport] 📥 Received SEEK command: ${newPos}s`);
        
        // 1. UPDATE STORE (Visuals) - Always update so scrubber reflects position
        const storeSeek = useMaestroStore.getState();
        storeSeek.updateTime(newPos, storeSeek.duration);
        console.log(`[ClientTransport] 📊 Store updated: position=${newPos}s`);
        
        // 2. UPDATE AUDIO ENGINE (Sound) - Browser or Connector local zone
        if ((!this.hasNativeBridge() || this.isConnectorLocalZone()) && this.audioElement) {
          console.log(`[ClientTransport] 🎧 Applying seek to HTML5 Audio: ${newPos}s`);
          try {
            this.audioElement.currentTime = newPos;
            console.log(`[ClientTransport] ✅ Audio element seeked to ${newPos}s`);
          } catch (err) {
            console.error('[ClientTransport] ❌ Audio seek failed:', err);
          }
        } else if (this.hasNativeBridge()) {
          console.log('[ClientTransport] 📱 iPhone Remote: UI updated only (no local audio)');
        } else {
          console.warn('[ClientTransport] ⚠️ No audio element registered');
        }
        
        // 3. Notify callbacks (for any external listeners)
        this.positionCallbacks.forEach((cb) => cb(newPos, storeSeek.duration));
        break;
      }
      
      // -----------------------------------------------------------------------
      // LOCAL FILE REQUEST (from remote client via BATON)
      // Home Connector registers the file and replies with a tunnel URL.
      // iOS PARITY: iOS sends requestLocalFile; the Connector responds with
      // localFileReady containing the Cloudflare Tunnel URL.
      // -----------------------------------------------------------------------
      case 'requestLocalFile': {
        console.log(`[ClientTransport] 📂 Remote requestLocalFile for: ${payload.filePath}`);
        import('../lib/playback-bridge').then(({ handleIncomingFileRequest }) => {
          handleIncomingFileRequest(payload, this.getWebSocket());
        });
        break;
      }
      
      default:
        console.log(`[ClientTransport] ⚠️ Unhandled remote command: ${cmd}`);
    }
  }
  
  // ---------------------------------------------------------------------------
  // CONNECTION MANAGEMENT
  // ---------------------------------------------------------------------------
  
  /**
   * Check if native bridge is available (iOS or Maestro)
   */
  private hasNativeBridge(): boolean {
    const p = detectPlatform();
    if (p.isIOSNativeApp || p.isTauriDesktop) return true;
    return !!(window as any).__MAESTRO_BRIDGE__;
  }
  
  /**
   * Check if running on the Maestro with a local zone selected.
   * When true, transport commands should bypass the bridge and play through
   * WebAudioRenderer directly. Discovery/multizone/library still use the bridge.
   */
  private isConnectorLocalZone(): boolean {
    if (!(window as any).__MAESTRO_CONNECTOR__) return false;
    const store = useMaestroStore.getState();
    const activeZone = store.discovery?.activeZone;
    if (!activeZone) return true; // default to local if no zone selected yet
    const id = activeZone.id || '';
    if (id === 'this_computer' || id === 'local_computer' || id === 'this_device') return true;
    const t = (activeZone as any).type || (activeZone as any).zone_type || '';
    return t === 'local' || t === 'browser';
  }
  
  /**
   * Check if the playback bridge (Connector OR iOS native) owns the active renderer.
   * When true, ClientTransportHandler must NOT send transport commands
   * (play/pause/stop/seek/volume) in response to WebSocket events —
   * the bridge exclusively controls that renderer via Tauri IPC or native bridge.
   */
  private isConnectorRendererActive(): boolean {
    const store = useMaestroStore.getState();
    const mode = getBridgeMode();
    if (!isBridgeAvailable() || (mode !== 'connector' && mode !== 'ios-native')) {
      return false;
    }
    // During track transitions activeConnectorRenderer can briefly clear; rendererHasStream
    // still means the bridge owns output — ignore WS playback echoes (pause) in that window.
    return !!store.activeConnectorRenderer || !!store.rendererHasStream;
  }

  public markConnectorPlaySent(): void {
    this._lastConnectorPlaySentAt = Date.now();
  }

  public markBridgePlayedTrack(trackId: string | null): void {
    this._lastBridgePlayedTrackId = trackId;
  }

  /**
   * Connect to the Control Bridge
   * 
   * CRITICAL FIX: On iOS, we need BOTH:
   * - Native Bridge: For local hardware control (volume, lock screen)
   * - WebSocket: For server sync (so other devices can see changes)
   * 
   * Previously, iOS would skip WebSocket entirely, breaking multi-device sync.
   */
  async connect(url: string): Promise<void> {
    if (!(await this.authAllowsConnect())) {
      console.log('[ClientTransport] Connect deferred — auth not ready for BATON');
      return;
    }
    if (this.reconnectHalted) {
      console.log('[ClientTransport] Reconnect halted — connect ignored');
      return;
    }
    if (this.connectInFlight) {
      console.log('[ClientTransport] Connect already in flight');
      return;
    }
    // Check if already connected
    if (this.connectionState === 'connected') {
      if (this.transportMode === 'native' || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
        console.log('[ClientTransport] Already connected via', this.transportMode);
        return;
      }
    }
    
    this.serverUrl = url;
    this.setConnectionState('connecting');
    this.connectInFlight = true;

    const hasNative = this.hasNativeBridge();

    if (hasNative) {
      console.log('[ClientTransport] 📲 iOS Native Bridge detected - setting up dual routing');
      this.transportMode = 'native';
      this.setupNativeMessageListener();
    }

    console.log('[ClientTransport] 🌐 Establishing WebSocket connection for server sync...');
    try {
      await this.connectWebSocket(url);

      if (hasNative) {
        await this.sendHandshake();
        console.log('[ClientTransport] ✅ Dual routing enabled: Native + WebSocket');
      }
    } finally {
      this.connectInFlight = false;
    }
  }
  
  /**
   * Connect via iOS native bridge (LEGACY - now handled in connect())
   */
  private async connectNative(): Promise<void> {
    console.log('[ClientTransport] 📲 iOS Native Bridge detected');
    
    this.transportMode = 'native';
    this.setConnectionState('connected');
    
    // Set up listener for messages FROM Swift
    this.setupNativeMessageListener();
    
    // Connect discovery handler for native iOS (discovery happens locally on device)
    // Pass null since we're using native bridge, not WebSocket
    getClientDiscoveryHandler().connect(null);
    console.log('[ClientTransport] 🔍 DiscoveryHandler initialized for native iOS');
    
    // Send handshake
    await this.sendHandshake();
    
    console.log('[ClientTransport] 📲 Native bridge connected');
  }
  
  /**
   * Set up listener for messages from iOS native code
   * iOS sends messages via window.postMessage or by calling a global function
   * 
   * Note: The primary global function (receiveNativeBatonEvent) is already set up
   * in the constructor. This method adds the window.postMessage listener.
   */
  private setupNativeMessageListener(): void {
    // Remove existing listener if any
    if (this.nativeMessageListener) {
      window.removeEventListener('message', this.nativeMessageListener);
    }
    
    // Create new listener for window.postMessage (fallback mechanism)
    this.nativeMessageListener = (event: MessageEvent) => {
      // Only process messages from our origin or from native (null origin)
      if (event.origin !== window.location.origin && event.origin !== 'null' && event.origin !== '') {
        return;
      }
      
      try {
        // Parse message data
        const data = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
        
        // Check if this looks like a BATON message
        if (data.includes('"type"') && (data.includes('"response"') || data.includes('"event"'))) {
          console.log('[ClientTransport] 📲 Native message received via postMessage');
          this.handleMessage(data);
        }
      } catch (e) {
        // Ignore non-BATON messages
      }
    };
    
    window.addEventListener('message', this.nativeMessageListener);
    
    console.log('[ClientTransport] 📲 Native message listeners ready');
    console.log('[ClientTransport] 📲 Swift should call: webView.evaluateJavaScript("receiveNativeBatonEvent(\'...\')") ');
  }
  
  /**
   * Connect via WebSocket
   * Includes userId, deviceId, and deviceType in the URL for room management
   */
  private connectWebSocket(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Add userId, deviceId, and deviceType to the URL for server-side room management
        const userId = getUserId();
        const deviceId = getDeviceId();
        const deviceType = getDeviceType();
        
        const separator = url.includes('?') ? '&' : '?';
        const fullUrl = `${url}${separator}userId=${encodeURIComponent(userId)}&deviceId=${encodeURIComponent(deviceId)}&deviceType=${encodeURIComponent(deviceType)}`;
        
        console.log('[BATON:ws-connect]', { deviceId, deviceType, userId: userId.slice(0, 8) + '...', platform: detectPlatform().deviceName });
        this.ws = new WebSocket(fullUrl);
        // Only set transportMode to websocket if not already native (iOS dual-routing)
        if (this.transportMode !== 'native') {
          this.transportMode = 'websocket';
        }
        
        this.ws.onopen = () => {
          this.connectInFlight = false;
          this.noteConnectSuccess();
          const rc = this.reconnectAttempts;
          this.setConnectionState('connected');
          this.reconnectAttempts = 0;
          this.reconnectCooldownCycles = 0;
          this.reconnectHalted = false;
          this.reconnectHaltReason = 'none';
          this.clearReconnectTimer();
          diagnosticLog('info', 'protocol', 'BATON WebSocket connected', {
            url: fullUrl.split('?')[0] + '?…',
            reconnectCount: rc,
          });

          // SyncHandler must bind on every new socket — reconnect used to leave a stale
          // closed WebSocket on the handler while store subscriptions kept running (leak + no push).
          getSyncHandler().connect(this.ws!);
          console.log('[ClientTransport] 🔗 SyncHandler bound to WebSocket (connect/reconnect)');
          
          // Connect ViewHandler to shared WebSocket
          getClientViewHandler().connect(this.ws!);
          console.log('[ClientTransport] 👁️ ViewHandler connected to WebSocket');
          
          // Connect MetadataHandler to shared WebSocket
          getMetadataHandler().connect(this.ws!);
          console.log('[ClientTransport] 📚 MetadataHandler connected to WebSocket');
          
          // Connect DiscoveryHandler to shared WebSocket
          getClientDiscoveryHandler().connect(this.ws!);
          console.log('[ClientTransport] 🔍 DiscoveryHandler connected to WebSocket');
          
          getClientMultizoneHandler().connect(this.ws!);
          console.log('[ClientTransport] 🔊 MultizoneHandler connected to WebSocket');

          this.startKeepalive();
          
          this.sendHandshake();
          resolve();
        };
        
        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };
        
        this.ws.onerror = (error) => {
          console.error('WebSocket connection error:', error);
          recordBatonWebSocketDiagnostic(
            'error',
            `BATON WebSocket error: ${String((error as any)?.message || 'error')}`,
            {
              event: 'error' as const,
              code: undefined,
              reason: String((error as any)?.message || 'error'),
              readyState: this.ws?.readyState,
            },
          );
        };
        
        this.ws.onclose = (event) => {
          this.connectInFlight = false;
          this.stopKeepalive();
          recordBatonWebSocketDiagnostic(
            'close',
            `BATON WebSocket close: ${event.reason || 'closed'}`,
            {
              event: 'close' as const,
              code: event.code,
              reason: event.reason || '',
              readyState: this.ws?.readyState,
            },
          );
          if (isAuthWebSocketClose(event.code, event.reason || '')) {
            void this.handleAuthWebSocketClose(event.code, event.reason || '');
            if (this.connectionState === 'connecting') {
              reject(new Error(`Connection rejected: code ${event.code}`));
            }
            return;
          }
          this.noteConnectFailure();
          this.handleDisconnect();
          if (this.connectionState === 'connecting') {
            reject(new Error(`Connection failed: code ${event.code}`));
          }
        };
        
      } catch (error) {
        console.error('❌ [ClientTransport] WebSocket connection EXCEPTION:', error);
        this.noteConnectFailure();
        this.setConnectionState('disconnected');
        reject(error);
      }
    });
  }
  
  /**
   * Disconnect from the server
   */
  disconnect(): void {
    this.reconnectHalted = true;
    this.reconnectHaltReason = 'explicit';
    this.reconnectAttempts = this.maxReconnectAttempts;
    this.reconnectCooldownCycles = this.maxReconnectCooldownCycles;
    this.clearReconnectTimer();
    
    if (this.reconnectCooldownTimer) {
      clearTimeout(this.reconnectCooldownTimer);
      this.reconnectCooldownTimer = null;
    }
    
    // Disconnect ViewHandler from WebSocket
    getClientViewHandler().disconnect();
    console.log('[ClientTransport] 👁️ ViewHandler disconnected');
    
    // Disconnect MetadataHandler from WebSocket
    getMetadataHandler().disconnect();
    console.log('[ClientTransport] 📚 MetadataHandler disconnected');
    
    // Disconnect DiscoveryHandler from WebSocket
    getClientDiscoveryHandler().disconnect();
    console.log('[ClientTransport] 🔍 DiscoveryHandler disconnected');
    
    getClientMultizoneHandler().disconnect();
    console.log('[ClientTransport] 🔊 MultizoneHandler disconnected');
    
    // Clean up WebSocket — null handlers BEFORE close to prevent
    // handleDisconnect() from firing and creating a ghost cooldown timer
    // that would reconnect 15s later without any React subscriptions.
    if (this.ws) {
      this.stopKeepalive();
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.setConnectionState('disconnected');
    
    // Clean up native listener
    if (this.nativeMessageListener) {
      window.removeEventListener('message', this.nativeMessageListener);
      this.nativeMessageListener = null;
    }
    
    // Clean up global bridge functions
    if ((window as any).receiveNativeBatonEvent) {
      delete (window as any).receiveNativeBatonEvent;
    }
    if ((window as any).batonReceiveEvent) {
      delete (window as any).batonReceiveEvent;
    }
    
    this.transportMode = 'none';
    this.setConnectionState('disconnected');
    this.isHandlerReady = false;
    this._awaitingHandshakeResponse = false;
    _requiresStreamUrlRefresh = false;
    
    // Clear pending commands
    this.pendingCommands.forEach((pending) => {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Disconnected'));
    });
    this.pendingCommands.clear();
  }
  
  /**
   * Get the underlying WebSocket for reuse by other modules
   * ViewHandler automatically uses this via getClientViewHandler().connect()
   * Returns null if WebSocket is not connected
   */
  getWebSocket(): WebSocket | null {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return this.ws;
    }
    return null;
  }
  
  /**
   * Check if WebSocket is connected and ready
   */
  isWebSocketReady(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /** Lightweight app-level ping to prevent server idle reap (~61s). */
  private startKeepalive(): void {
    this.stopKeepalive();
    this.keepaliveTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        } catch {
          /* best effort */
        }
      }
    }, 30_000);
  }

  private stopKeepalive(): void {
    if (!this.keepaliveTimer) return;
    clearInterval(this.keepaliveTimer);
    this.keepaliveTimer = null;
  }
  
  /**
   * Stop WebSocket reconnect loops (session dead or explicit teardown).
   */
  haltReconnectLoops(): void {
    this.reconnectHalted = true;
    this.reconnectHaltReason = 'auth';
    this.reconnectAttempts = this.maxReconnectAttempts;
    this.reconnectCooldownCycles = this.maxReconnectCooldownCycles;
    this.clearReconnectTimer();
    if (this.reconnectCooldownTimer) {
      clearTimeout(this.reconnectCooldownTimer);
      this.reconnectCooldownTimer = null;
    }
  }

  private noteConnectSuccess(): void {
    this.consecutiveConnectFailuresSinceSuccess = 0;
  }

  private noteConnectFailure(): void {
    this.consecutiveConnectFailuresSinceSuccess += 1;
    void this.maybeProbeAuthAfterConnectFailures();
  }

  /**
   * Burned tokens often present as bare 1006 — after N consecutive WS failures with no
   * intervening success, force HTTP refresh validation and surface re-auth on rejection.
   */
  private async maybeProbeAuthAfterConnectFailures(): Promise<void> {
    const threshold = BATON_RECONNECT_POLICY.AUTH_PROBE_AFTER_CONSECUTIVE_FAILURES;
    if (this.consecutiveConnectFailuresSinceSuccess !== threshold) return;
    if (this.authProbeInFlight || this.reconnectHalted) return;

    // Healthy JWT + live session → WS failures are transport/network, not auth.
    if (!shouldProbeAuthAfterWsFailures(canAttemptBatonConnect())) return;

    void import('../lib/sessionDead').then(async ({ isSessionDead }) => {
      if (isSessionDead()) return;
      this.authProbeInFlight = true;
      try {
        const { probeSessionAuthAfterBatonFailures } = await import('../services/api');
        const dead = await probeSessionAuthAfterBatonFailures('ClientTransport:ws-failure-probe');
        if (dead) {
          console.warn('[ClientTransport] HTTP auth probe failed — halting BATON reconnect');
          this.haltReconnectLoops();
          this.reconnectHaltReason = 'auth';
          const isNativeMode = this.transportMode === 'native';
          if (!isNativeMode) this.setConnectionState('disconnected');
        }
      } catch (e) {
        console.warn('[ClientTransport] HTTP auth probe error:', e);
      } finally {
        this.authProbeInFlight = false;
      }
    });
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private async handleAuthWebSocketClose(code: number, reason: string): Promise<void> {
    // Explicit WS auth signal (1008/4401 or reason text). Burned-token storms that
    // present as 1006 with empty reason are handled by api.ts markSessionDead instead.
    console.warn('[ClientTransport] BATON WebSocket auth failure — reconnect stopped', { code, reason });
    this.clearReconnectTimer();
    this.haltReconnectLoops();
    this.isHandlerReady = false;
    const isNativeMode = this.transportMode === 'native';
    if (!isNativeMode) this.setConnectionState('disconnected');
    try {
      const { markSessionDead } = await import('../lib/sessionDead');
      markSessionDead(`baton_ws_auth:${code}`);
    } catch { /* ignore */ }
  }

  /**
   * Handle disconnection and attempt reconnect
   * Note: Only applies to WebSocket connections, native bridge is always available
   */
  private handleDisconnect(): void {
    this.isHandlerReady = false;

    void import('../lib/sessionDead').then(({ isSessionDead }) => {
      if (isSessionDead() || this.reconnectHalted) {
        const isNativeMode = this.transportMode === 'native';
        if (!isNativeMode) this.setConnectionState('disconnected');
        console.log('[ClientTransport] Session dead or reconnect halted — WebSocket reconnect stopped');
        return;
      }
      this.scheduleReconnect();
    }).catch(() => {
      if (!this.reconnectHalted) this.scheduleReconnect();
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectHalted || this.reconnectTimer) return;
    void this.authAllowsConnect().then((allowed) => {
      if (!allowed) {
        console.log('[ClientTransport] Reconnect deferred — auth not ready for BATON');
        const isNativeMode = this.transportMode === 'native';
        if (!isNativeMode) this.setConnectionState('disconnected');
        return;
      }
      this.scheduleReconnectInner();
    });
  }

  private scheduleReconnectInner(): void {
    if (this.reconnectHalted || this.reconnectTimer) return;

    const isNativeMode = this.transportMode === 'native';
    if (!isNativeMode && typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.networkOffline = true;
      this.setConnectionState('reconnecting');
      console.log('[ClientTransport] Reconnect deferred — browser offline');
      return;
    }
    this.networkOffline = false;

    const maxAttempts = isNativeMode
      ? BATON_RECONNECT_POLICY.NATIVE_MAX_ATTEMPTS
      : this.maxReconnectAttempts;
    const baseDelay = isNativeMode
      ? BATON_RECONNECT_POLICY.NATIVE_BASE_DELAY_MS
      : this.reconnectDelay;

    if (this.reconnectAttempts < maxAttempts) {
      if (!isNativeMode) this.setConnectionState('reconnecting');
      this.reconnectAttempts += 1;

      const delay = computeReconnectDelayMs(this.reconnectAttempts, baseDelay);
      console.log(
        `[ClientTransport] WebSocket reconnecting in ${delay}ms `
        + `(attempt ${this.reconnectAttempts}/${maxAttempts})`,
      );

      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        void this.runReconnectAttempt();
      }, delay);
      return;
    }

    this.reconnectCooldownCycles += 1;
    if (this.reconnectCooldownCycles >= this.maxReconnectCooldownCycles) {
      console.log('[ClientTransport] WebSocket reconnect stopped after max cooldown cycles');
      this.reconnectHalted = true;
      this.reconnectHaltReason = 'exhausted';
      if (!isNativeMode) this.setConnectionState('error');
      return;
    }

    if (!isNativeMode) this.setConnectionState('reconnecting');
    console.log('[ClientTransport] Max WebSocket reconnect attempts reached — cooling down');

    if (!this.reconnectCooldownTimer) {
      const cooldown = isNativeMode
        ? BATON_RECONNECT_POLICY.NATIVE_COOLDOWN_MS
        : BATON_RECONNECT_POLICY.COOLDOWN_MS;
      this.reconnectCooldownTimer = setTimeout(() => {
        this.reconnectCooldownTimer = null;
        void import('../lib/sessionDead').then(({ isSessionDead }) => {
          if (isSessionDead() || this.reconnectHalted) return;
          this.reconnectAttempts = 0;
          this.scheduleReconnect();
        });
      }, cooldown);
    }
  }

  private async runReconnectAttempt(): Promise<void> {
    if (this.reconnectHalted || this.connectInFlight) return;
    if (!(await this.authAllowsConnect())) {
      console.warn('[ClientTransport] WebSocket reconnect deferred — auth not ready');
      const isNativeMode = this.transportMode === 'native';
      if (!isNativeMode) this.setConnectionState('disconnected');
      return;
    }
    try {
      const { isSessionDead } = await import('../lib/sessionDead');
      if (isSessionDead()) return;
      const freshUrl = getWebSocketUrl();
      if (!freshUrl.includes('token=')) {
        console.warn('[ClientTransport] WebSocket reconnect deferred — no auth token in URL');
        const isNativeMode = this.transportMode === 'native';
        if (!isNativeMode) this.setConnectionState('disconnected');
        return;
      }
      this.connectInFlight = true;
      await this.connectWebSocket(freshUrl);
    } catch (err) {
      console.error('[ClientTransport] WebSocket reconnect failed:', err);
      this.connectInFlight = false;
      this.handleDisconnect();
    }
  }
  
  /** Allow reconnect after user re-authenticates (clears auth halt latch). */
  resetReconnectAfterAuth(): void {
    this.reconnectHalted = false;
    this.reconnectHaltReason = 'none';
    this.reconnectAttempts = 0;
    this.reconnectCooldownCycles = 0;
    this.consecutiveConnectFailuresSinceSuccess = 0;
    this.authProbeInFlight = false;
    this.clearReconnectTimer();
    if (this.reconnectCooldownTimer) {
      clearTimeout(this.reconnectCooldownTimer);
      this.reconnectCooldownTimer = null;
    }
  }

  /**
   * Tear down a stale WebSocket without halting future reconnect (post sign-in restart).
   */
  private teardownWebSocketForReconnect(): void {
    this.clearReconnectTimer();
    if (this.reconnectCooldownTimer) {
      clearTimeout(this.reconnectCooldownTimer);
      this.reconnectCooldownTimer = null;
    }
    getClientViewHandler().disconnect();
    getMetadataHandler().disconnect();
    getClientDiscoveryHandler().disconnect();
    getClientMultizoneHandler().disconnect();
    getSyncHandler().disconnect();
    if (this.ws) {
      this.stopKeepalive();
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      try {
        this.ws.close(1000, 'Auth restart');
      } catch { /* ignore */ }
      this.ws = null;
    }
    this.connectInFlight = false;
    this.isHandlerReady = false;
    if (this.transportMode !== 'native') {
      this.setConnectionState('disconnected');
    }
  }

  /** Post sign-in: reset backoff state, close stale socket, connect with fresh token. */
  async reconnectAfterAuth(url?: string): Promise<void> {
    if (!(await this.authAllowsConnect())) {
      console.log('[ClientTransport] reconnectAfterAuth deferred — auth not ready');
      return;
    }
    this.resetReconnectAfterAuth();
    this.teardownWebSocketForReconnect();
    const wsUrl = url || getWebSocketUrl();
    if (!wsUrl.includes('token=')) {
      console.warn('[ClientTransport] reconnectAfterAuth deferred — no token in URL');
      return;
    }
    await this.connect(wsUrl);
  }

  private async authAllowsConnect(): Promise<boolean> {
    try {
      const { isSessionDead } = await import('../lib/sessionDead');
      if (isSessionDead()) return false;
    } catch { /* ignore */ }
    return canAttemptBatonConnect();
  }

  /**
   * Resume BATON reconnect after a network outage halt (not auth/explicit teardown).
   * @param source `online` — browser came back; immediate retry. `manual` — user Retry.
   */
  async resumeReconnectAfterNetworkRecovery(source: 'online' | 'manual' = 'online'): Promise<void> {
    const { isSessionDead } = await import('../lib/sessionDead');
    if (isSessionDead()) return;
    if (this.reconnectHaltReason === 'auth') return;
    if (this.reconnectHaltReason === 'explicit') return;
    if (this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN) return;

    this.networkOffline = false;
    this.reconnectHalted = false;
    this.reconnectHaltReason = 'none';
    this.reconnectAttempts = 0;
    this.reconnectCooldownCycles = 0;
    if (source === 'manual') {
      this.consecutiveConnectFailuresSinceSuccess = 0;
      this.authProbeInFlight = false;
    }
    this.clearReconnectTimer();
    if (this.reconnectCooldownTimer) {
      clearTimeout(this.reconnectCooldownTimer);
      this.reconnectCooldownTimer = null;
    }

    const isNativeMode = this.transportMode === 'native';
    if (!isNativeMode) this.setConnectionState('reconnecting');
    console.log(`[ClientTransport] Resuming BATON reconnect (${source})`);
    void this.runReconnectAttempt();
  }

  /** Pause reconnect scheduling while the browser reports offline. */
  noteNetworkOffline(): void {
    this.networkOffline = true;
    this.clearReconnectTimer();
    const isNativeMode = this.transportMode === 'native';
    if (!isNativeMode && this.connectionState !== 'connected') {
      this.setConnectionState('reconnecting');
    }
  }

  /** Whether the user can manually retry BATON sync (network halt, not auth). */
  canManualReconnect(): boolean {
    if (this.reconnectHaltReason === 'auth' || this.reconnectHaltReason === 'explicit') return false;
    if (this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN) return false;
    return true;
  }

  /**
   * Update and broadcast connection state
   */
  private setConnectionState(state: ClientConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.connectionCallbacks.forEach((cb) => cb(state));
    }
  }
  
  // ---------------------------------------------------------------------------
  // HANDSHAKE
  // ---------------------------------------------------------------------------
  
  /**
   * Send init command to establish session
   * 
   * CRITICAL: This handshake is what makes the browser a "first-class citizen"
   * The server will respond with the current state (playback, volume, track)
   * so the browser syncs immediately, even if it connects AFTER the iPhone.
   */
  private async sendHandshake(): Promise<void> {
    const platform = this.detectPlatform();
    const formFactor = this.detectFormFactor();
    const isConnector = !!(window as any).__MAESTRO_CONNECTOR__;
    const deviceType = isConnector ? 'desktop-connector'
      : this.hasNativeBridge() ? 'ios-remote'
      : 'web-renderer';
    
    console.log('[ClientTransport] 🤝 Sending handshake to server...');
    console.log(`[ClientTransport]    Device: ${deviceType} (${platform}/${formFactor})`);
    
    this._awaitingHandshakeResponse = true;

    try {
      const clientInstanceId = (() => {
        try {
          const key = 'maestro_client_instance_id';
          const existing = window.sessionStorage.getItem(key);
          if (existing) return existing;
          const id = `ci_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
          window.sessionStorage.setItem(key, id);
          return id;
        } catch {
          return `ci_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
        }
      })();

      const response = await this.sendCommand({
        cmd: 'init',
        protocolVersion: PROTOCOL_VERSION,
        clientInstanceId,
        device: {
          platform: platform,
          formFactor: formFactor,
          deviceType: deviceType,
          tier: 'standard',
          networkType: 'unknown',
          networkMetered: false,
          locale: navigator.language || 'en-US',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          appVersion: '1.0.0',
        },
      } as CommandPayload);
      
      if (isSuccess(response.payload)) {
        console.log('[ClientTransport] ✅ Handshake successful via', this.transportMode);
        console.log('[ClientTransport] 📥 Server will now send current state...');
        this.isHandlerReady = true;
      } else {
        console.error('[ClientTransport] ❌ Handshake failed:', response.payload);
        this._awaitingHandshakeResponse = false;
      }
    } catch (error) {
      console.error('[ClientTransport] ❌ Handshake error:', error);
      this._awaitingHandshakeResponse = false;
    }
  }
  
  /**
   * Detect device form factor
   */
  private detectFormFactor(): 'phone' | 'tablet' | 'desktop' {
    const width = window.innerWidth;
    if (width < 768) return 'phone';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }
  
  /**
   * Detect platform (iOS native vs web)
   */
  private detectPlatform(): 'ios' | 'android' | 'web' | 'macos' | 'windows' {
    if (this.transportMode === 'native') {
      // If using native bridge, we're in a native app
      // Check user agent for platform hints
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes('iphone') || ua.includes('ipad')) return 'ios';
      if (ua.includes('android')) return 'android';
      if (ua.includes('mac')) return 'macos';
      if (ua.includes('windows')) return 'windows';
    }
    return 'web';
  }
  
  // ---------------------------------------------------------------------------
  // COMMAND SENDING
  // ---------------------------------------------------------------------------
  
  /**
   * Check for native bridge at runtime (iOS or Maestro)
   * This is the authoritative check - don't rely on transportMode
   */
  private getIOSBridge(): any {
    if (!detectPlatform().isIOSNativeApp) return null;
    return (window as any).webkit?.messageHandlers?.baton
      || (window as any).__MAESTRO_BRIDGE__;
  }
  
  /**
   * Send a message via the appropriate transport
   * 
   * DUAL ROUTING: On iOS, send to BOTH:
   * - iOS Native Bridge: For local hardware (volume, lock screen, background audio)
   * - WebSocket: For server sync (multi-device track/state sync)
   * 
   * CONNECTOR LOCAL ZONE: When the Connector is active with "This Computer" selected,
   * transport commands SKIP the bridge — WebAudioRenderer handles audio locally.
   * Non-transport commands (discovery, multizone, library) still use the bridge.
   */
  private sendViaTransport(json: string, cmd?: string, handler?: string): boolean {
    const iosBridge = this.getIOSBridge();
    const hasWebSocket = this.ws && this.ws.readyState === WebSocket.OPEN;
    let sent = false;
    
    // Connector + local zone: skip bridge ONLY for playback commands.
    // init, status, setVolume, etc. must always reach the bridge.
    const PLAYBACK_CMDS = ['play', 'pause', 'stop', 'seek', 'next', 'previous'];
    const skipBridgeForLocal = handler === 'transport'
      && this.isConnectorLocalZone()
      && PLAYBACK_CMDS.includes(cmd || '');
    const connectorNetworkTransportCmd = handler === 'transport'
      && !!(window as any).__MAESTRO_CONNECTOR__
      && !this.isConnectorLocalZone()
      && PLAYBACK_CMDS.includes(cmd || '');
    
    if (iosBridge && !skipBridgeForLocal) {
      iosBridge.postMessage(json);
      if (this.transportMode !== 'native') {
        this.transportMode = 'native';
      }
      sent = true;
      
      const storeState = useMaestroStore.getState();
      const activeZone = storeState.discovery?.activeZone;
      if (activeZone && activeZone.type !== 'local') {
        console.log(`[ClientTransport] 📱 Sent '${cmd || 'command'}' to native → ${activeZone.name} (${activeZone.type})`);
      }
    } else if (skipBridgeForLocal) {
      console.log(`[ClientTransport] 🖥️ Connector local zone — '${cmd}' handled by WebAudioRenderer, skipping bridge`);
    }
    
    // WebSocket — state sync across devices
    if (hasWebSocket && !connectorNetworkTransportCmd) {
      this.ws!.send(json);
      sent = true;
    } else if (connectorNetworkTransportCmd) {
      console.log(`[ClientTransport] 📡 Connector network zone — '${cmd}' sent via native bridge only`);
    }
    
    return sent;
  }
  
  /**
   * Send a command to the Control Bridge
   * Wraps payload in standard BATON envelope
   * 
   * ROUTING LOGIC:
   * 1. If iOS native bridge exists (window.__maestroNative) → Send to Swift
   * 2. If Tauri/Connector is available → Send via Tauri IPC
   * 3. Otherwise → Send via WebSocket to Control Bridge server
   */
  async sendCommand(
    payload: CommandPayload,
    handler: HandlerId = 'transport',
    location?: HandlerLocation
  ): Promise<BatonMessage<ResponsePayload>> {
    // Check for iOS bridge FIRST (fresh check every time)
    const iosBridge = this.getIOSBridge();
    const hasWebSocket = this.ws && this.ws.readyState === WebSocket.OPEN;
    
    // Can send if we have either transport
    const canSend = iosBridge || hasWebSocket;
    
    if (!canSend) {
      throw new Error('Not connected to server');
    }
    
    // -------------------------------------------------------------------------
    // LOCAL BROWSER PLAYBACK PREP (This Computer / This Device)
    // Ensure the <audio> element has a src BEFORE server echoes playbackState.
    // Without this, WebAudioRenderer receives "playing" but cannot play (no src).
    // -------------------------------------------------------------------------
    this.maybeDoLocalPlaybackPrep(handler, payload, iosBridge);

    // Create BATON message envelope
    const message = createCommand(handler, payload, { location });
    
    return new Promise((resolve, reject) => {
      // Set up timeout
      const timeout = setTimeout(() => {
        this.pendingCommands.delete(message.id);
        reject(new Error(`Command timeout: ${(payload as any).cmd}`));
      }, this.commandTimeout);
      
      // Store pending command
      this.pendingCommands.set(message.id, { resolve, reject, timeout });
      
      // Send message via appropriate transport
      const json = JSON.stringify(message);
      const cmd = (payload as any).cmd || 'unknown';
      
      const sent = this.sendViaTransport(json, cmd, handler);
      if (!sent) {
        clearTimeout(timeout);
        this.pendingCommands.delete(message.id);
        reject(new Error('Failed to send message'));
      }
    });
  }
  
  /**
   * Send command without waiting for response (fire-and-forget)
   * Same routing logic as sendCommand
   */
  sendCommandAsync(
    payload: CommandPayload,
    handler: HandlerId = 'transport',
    location?: HandlerLocation
  ): void {
    // Check for iOS bridge FIRST (fresh check every time)
    const iosBridge = this.getIOSBridge();
    const hasWebSocket = this.ws && this.ws.readyState === WebSocket.OPEN;
    
    if (!iosBridge && !hasWebSocket) {
      console.error('[ClientTransport] Cannot send: no transport available');
      return;
    }
    
    this.maybeDoLocalPlaybackPrep(handler, payload, iosBridge);

    const message = createCommand(handler, payload, { location });
    const json = JSON.stringify(message);
    const cmd = (payload as any).cmd || 'unknown';
    
    this.sendViaTransport(json, cmd, handler);
  }
  
  // ---------------------------------------------------------------------------
  // MESSAGE HANDLING
  // ---------------------------------------------------------------------------
  
  /**
   * Parse and handle incoming WebSocket message
   */
  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);

      if (this.isTypeOnlyLocalCoreMessage(message)) {
        this.handleTypeOnlyLocalCoreMessage(message);
        return;
      }
      
      // Validate BATON message format
      if (!validateMessage(message)) {
        console.warn('[ClientTransport] Invalid message format:', data);
        return;
      }
      
      const batonMessage = message as BatonMessage;
      
      switch (batonMessage.type) {
        case 'response':
          this.handleResponse(batonMessage as BatonMessage<ResponsePayload>);
          break;
        
        case 'event':
          this.handleEvent(batonMessage as BatonMessage<EventPayload>);
          break;
        
        case 'command':
          // BROWSER AS BATON DEVICE: Handle remote commands from server
          // Server forwards commands from other clients (e.g., iPhone → Server → Browser)
          console.log('[ClientTransport] 📥 Received command from server (forwarded)');
          this.handleRemoteCommand(batonMessage as BatonMessage<CommandPayload>);
          break;
      }
      
    } catch (error) {
      console.error('[ClientTransport] Failed to parse message:', error, data);
    }
  }
  
  /**
   * Handle response to a pending command
   */
  private handleResponse(message: BatonMessage<ResponsePayload>): void {
    // First, check if this is a response for the MetadataHandler
    // MetadataHandler sends commands directly and manages its own pending requests
    if (message.handler === 'metadata') {
      console.log(`[ClientTransport] 📚 Routing response to MetadataHandler: ${message.id}`);
      getMetadataHandler().handleMessage(message);
      return;
    }
    
    // Check if this is a response for the DiscoveryHandler
    if (message.handler === 'discovery') {
      console.log(`[ClientTransport] 🔍 Routing response to DiscoveryHandler: ${message.id}`);
      getClientDiscoveryHandler().handleResponse(message as any);
      return;
    }
    
    // Route multizone responses to MultizoneHandler
    if (message.handler === 'multizone' && message.type === 'response') {
      getClientMultizoneHandler().handleResponse(message as any);
      return;
    }
    
    const pending = this.pendingCommands.get(message.id);
    
    if (pending) {
      clearTimeout(pending.timeout);
      this.pendingCommands.delete(message.id);

      // When we see the handshake response, mark that the first play
      // must re-resolve the stream URL. BATON state flows through normally
      // — track info, playback state, position — but the audio layer won't
      // use the stale Qobuz URL; it will fetch a fresh one.
      if (this._awaitingHandshakeResponse) {
        this._awaitingHandshakeResponse = false;
        _requiresStreamUrlRefresh = true;
        console.log('[ClientTransport] handshake complete — stream URL refresh required for first play');
      }

      pending.resolve(message);
    } else {
      // This can happen if the UI navigates/reloads and drops a pending promise before the response arrives.
      // Avoid spamming the console unless debugging is enabled.
      try {
        const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
        if (dbg) console.warn('[ClientTransport] Received response for unknown command:', message.id);
      } catch {
        // ignore
      }
    }
  }
  
  /**
   * Handle incoming event from server
   * 
   * BATON Protocol: Events can come in two formats:
   * 1. Legacy: payload.event = 'volumeChanged' (handler = 'transport')
   * 2. New BATON: handler = 'volume', payload = { level, muted } (no event field)
   * 
   * Handler owns state updates - updates Zustand store directly.
   */
  private handleEvent(message: BatonMessage<EventPayload>): void {
    const payload = message.payload;
    const handler = message.handler;
    const eventType = 'event' in payload ? payload.event : null;
    
    console.log(`[ClientTransport] 📩 Event received: handler=${handler}, event=${eventType}`);
    console.log(`[ClientTransport] Full payload:`, payload);
    
    // Dispatch to generic listeners
    this.genericEventCallbacks.forEach((cb) => cb(message));
    
    // -------------------------------------------------------------------------
    // VIEW EVENTS: Route to ClientViewHandler
    // viewChanged, selectionChanged events from other clients
    // -------------------------------------------------------------------------
    if (handler === 'view') {
      console.log(`[ClientTransport] 👁️ Routing to ViewHandler: ${eventType}`);
      getClientViewHandler().handleEvent(payload as any);
      return;
    }
    
    // -------------------------------------------------------------------------
    // METADATA EVENTS: Route to MetadataHandler
    // artistMetadataLoaded, albumMetadataLoaded, trackMetadataLoaded
    // -------------------------------------------------------------------------
    if (handler === 'metadata') {
      console.log(`[ClientTransport] 📚 Routing to MetadataHandler: ${eventType}`);
      getMetadataHandler().handleEvent(payload as any);
      return;
    }
    
    // -------------------------------------------------------------------------
    // DISCOVERY EVENTS: Route to DiscoveryHandler
    // zonesChanged, activeZoneChanged, scanComplete
    // -------------------------------------------------------------------------
    if (handler === 'discovery') {
      console.log(`[ClientTransport] 🔍 Routing to DiscoveryHandler: ${eventType}`);
      getClientDiscoveryHandler().handleEvent(payload as any);
      return;
    }
    
    // Route multizone events to MultizoneHandler
    if (message.handler === 'multizone') {
      console.log('[ClientTransport] 🔊 Routing multizone event to MultizoneHandler');
      getClientMultizoneHandler().handleEvent(payload as any);
      return;
    }
    
    // -------------------------------------------------------------------------
    // SYNC EVENTS: Cross-device session sync via ClientSyncHandler
    // -------------------------------------------------------------------------
    if (handler === 'sync') {
      getSyncHandler().handleSyncEvent(payload as Record<string, any>);
      return;
    }

    // -------------------------------------------------------------------------
    // LIBRARY EVENTS: localFileReady / localFileError from home Connector
    // iOS PARITY: These events resolve the tunnel URL for remote local library
    // playback. iOS receives the same events via this WebSocket path.
    // -------------------------------------------------------------------------
    if (handler === 'library') {
      console.log(`[ClientTransport] 📂 Library event received: ${eventType}`);
      if (eventType === 'localFileReady' || eventType === 'localFileError') {
        import('../lib/playback-bridge').then(({ handleRemoteFileEvent }) => {
          handleRemoteFileEvent(payload as any);
        });
      }
      return;
    }
    
    // -------------------------------------------------------------------------
    // QUEUE CONTROL EVENTS (from iOS for next/previous buttons)
    // Uses Zustand store's queue (where tracks are actually stored)
    // -------------------------------------------------------------------------
    if (handler === 'queue' && eventType === 'queueControl') {
      const action = 'action' in payload ? payload.action : null;
      console.log(`[ClientTransport] ⏭️ Queue control event: ${action}`);
      
      // Get current queue state from Zustand store
      const store = useMaestroStore.getState();
      const { queue, queueIndex, setQueueIndex, setCurrentTrack } = store;
      
      console.log(`[ClientTransport] 📋 Queue has ${queue.length} tracks, current index: ${queueIndex}`);
      
      let newIndex = queueIndex;
      let newTrack = null;
      
      if (action === 'next') {
        if (queueIndex < queue.length - 1) {
          newIndex = queueIndex + 1;
          newTrack = queue[newIndex];
          console.log(`[ClientTransport] ⏭️ Next track: ${newTrack?.track?.title}`);
        } else {
          console.log('[ClientTransport] ⏭️ No next track - at end of queue');
        }
      } else if (action === 'previous') {
        if (queueIndex > 0) {
          newIndex = queueIndex - 1;
          newTrack = queue[newIndex];
          console.log(`[ClientTransport] ⏮️ Previous track: ${newTrack?.track?.title}`);
        } else {
          console.log('[ClientTransport] ⏮️ No previous track - at start of queue');
        }
      }
      
      // If we have a new track, update state and send play command
      if (newTrack && newIndex !== queueIndex) {
        // Update store
        setQueueIndex(newIndex);
        setCurrentTrack(newTrack.track);
        
        const trackData = newTrack.track;
        const streamUrl = (trackData as any).streamUrl || '';
        
        console.log(`[ClientTransport] ▶️ Sending play command for: ${trackData.title}`);
        
        // QueueController.next()/previous() → TransportCommand.playUri() handles
        // sending the play command to the active renderer. Do NOT duplicate here.
      }
      return;
    }
    
    // -------------------------------------------------------------------------
    // VOLUME EVENTS (Handler owns state updates per BATON protocol)
    // Detect volumeChanged by either:
    //   - New format: handler === 'volume' with level/muted in payload
    //   - Legacy format: eventType === 'volumeChanged'
    // -------------------------------------------------------------------------
    const isVolumeEvent = 
      (handler === 'volume' && 'level' in payload && 'muted' in payload) ||
      eventType === 'volumeChanged';
    
    if (isVolumeEvent && 'level' in payload && 'muted' in payload) {
      if (this.isConnectorRendererActive()) {
        console.log('[ClientTransport] 🛡️ Connector owns renderer — ignoring volumeChanged from WebSocket');
        return;
      }

      const level = Math.floor(payload.level as number); // Ensure integer
      const muted = payload.muted as boolean;
      
      console.log(`📥 [ClientTransport] volumeChanged event: level=${level}, muted=${muted}`);

      const stVol = useMaestroStore.getState();
      const rn = stVol.discovery?.activeZone?.name || stVol.activeConnectorRenderer?.name || 'browser';
      diagnosticLog(
        'info',
        'volume',
        `Volume response from ${rn}: ok`,
        {
          renderer: rn,
          requestedLevel: stVol.volume,
          reportedLevel: level,
        },
      );
      
      // 1. HANDLER OWNS STATE: Update Zustand store directly
      const store = useMaestroStore.getState();
      store.setVolume(level);
      store.setMuted(muted);
      
      // 2. CRITICAL: Apply to HTML5 audio element (actual audio playback)
      // This ensures Browser obeys volume commands from iPhone
      if (this.audioElement) {
        this.audioElement.volume = level / 100;
        this.audioElement.muted = muted;
        console.log(`🔊 [ClientTransport] Applied to audio element: volume=${level / 100}, muted=${muted}`);
      } else {
        console.warn(`⚠️ [ClientTransport] No audio element registered - volume not applied to playback`);
      }
      
      // 3. Notify any registered callbacks (for legacy/external listeners)
      this.volumeCallbacks.forEach((cb) => cb(level, muted));
      return;
    }
    
    // -------------------------------------------------------------------------
    // POSITION EVENTS (Handler owns state updates per BATON protocol)
    // Server is source of truth - UI binds to store, not local timers
    // -------------------------------------------------------------------------
    if (eventType === 'positionChanged' && 'position' in payload && 'duration' in payload) {
      if (this.isConnectorRendererActive()) {
        return;
      }

      // Server no longer echoes position events to sender (broadcastToOthers).
      // Position updates only arrive from other devices — apply them.

      const position = Math.floor(payload.position as number);
      const duration = Math.floor(payload.duration as number);
      
      // CHECK POSITION LOCK: If user is scrubbing, ignore server position updates
      // This prevents the "snap back" issue where server position overwrites drag
      if (this.positionLocked) {
        console.log(`📍 [ClientTransport] positionChanged BLOCKED (position locked): ${position}s`);
        // Still notify callbacks so App.jsx knows about it (for logging)
        this.positionCallbacks.forEach((cb) => cb(position, duration));
        return; // Do NOT update store
      }
      
      console.log(`📍 [ClientTransport] positionChanged: ${position}s / ${duration}s`);
      
      // -----------------------------------------------------------------------
      // BROWSER AS RENDERER: Apply position to audio element if significantly different
      // This handles remote seek commands (iPhone → Server → Browser)
      // Also applies when Connector is active with local zone
      // -----------------------------------------------------------------------
      if ((!this.hasNativeBridge() || this.isConnectorLocalZone()) && this.audioElement) {
        const currentAudioTime = Math.floor(this.audioElement.currentTime);
        const positionDiff = Math.abs(currentAudioTime - position);
        
        // Only seek if position differs by more than 2 seconds (avoids tiny jumps)
        // Normal playback progression updates every 1s, so 2s threshold is safe
        if (positionDiff > 2) {
          console.log(`[ClientTransport] 🎧 Applying remote seek to: ${position} seconds (diff: ${positionDiff}s)`);
          try {
            this.audioElement.currentTime = position;
          } catch (err) {
            console.error('[ClientTransport] ❌ Remote seek failed:', err);
          }
        }
      }
      
      // HANDLER OWNS STATE: Update Zustand store directly
      // This ensures UI (including remote iPhone) shows server-authoritative position
      const store = useMaestroStore.getState();
      store.updateTime(position, duration);
      
      // Also notify any registered callbacks (for legacy/external listeners)
      this.positionCallbacks.forEach((cb) => cb(position, duration));
      return;
    }
    
    // -------------------------------------------------------------------------
    // OTHER EVENTS (Dispatch to specific listeners based on event type)
    // -------------------------------------------------------------------------
    switch (eventType) {
      case 'playbackStateChanged': {
        console.log(`[ClientTransport] 🎵 playbackStateChanged event received!`);

        if (this.isConnectorRendererActive()) {
          console.log('[ClientTransport] 🛡️ Connector owns renderer — ignoring playbackStateChanged from WebSocket');
          break;
        }

        rlog('CTH', 'playbackStateChanged', { state: 'state' in payload ? payload.state : '?' });

        const preStore = useMaestroStore.getState();
        const oldPlaybackState = preStore.playbackState;
        const newState = 'state' in payload ? (payload.state as PlaybackState) : null;
        console.log(`[ClientTransport] 🎵 New state: ${newState}`);
        if (!newState) {
          console.warn(`[ClientTransport] ⚠️ No state in payload, breaking`);
          break;
        }

        if (newState === 'paused' && TransportCommand.isInPlayUriPostSuccessSilenceWindow()) {
          console.log('[Sync] Ignoring stale pause — within 2s of playUri');
          break;
        }

        // Reconnect / QueueSession can echo stale "paused" while the native bridge still has
        // an active stream (same guard as TransportState + ClientSyncHandler). Without this,
        // isConnectorRendererActive() can be false for a tick and the WS path flips the store
        // to paused → UI/watchdog fight → pause/resume loop on flaky links.
        const pre = useMaestroStore.getState();
        if (
          newState === 'paused'
          && pre.rendererHasStream
          && (pre.playbackState === 'playing' || pre.playbackState === 'loading')
        ) {
          console.log('[ClientTransport] 🛡️ Ignoring WS playbackStateChanged pause — rendererHasStream + local playing');
          break;
        }

        diagnosticLog(
          'info',
          'transport',
          `Renderer state: ${newState} (was ${oldPlaybackState})`,
          {
            renderer: preStore.discovery?.activeZone?.name || preStore.activeConnectorRenderer?.name,
            newState,
            oldState: oldPlaybackState,
            trackId: preStore.currentTrack?.id,
          },
        );

        // Check if a network renderer is handling playback (from server)
        const networkRendererActive = 'networkRendererActive' in payload && payload.networkRendererActive === true;
        const activeRendererName = 'activeRendererName' in payload ? payload.activeRendererName : null;
        const activeRendererType = 'activeRendererType' in payload ? payload.activeRendererType : null;
        
        if (networkRendererActive) {
          console.log(`[ClientTransport] 🔊 Network renderer handling playback: ${activeRendererName} (${activeRendererType})`);
        }
        
        const storeState = useMaestroStore.getState();
        const zoneName = storeState.system?.zoneName || '';
        
        storeState.setPlaybackState(newState);
        this.playbackStateCallbacks.forEach((cb) => cb(newState));
        
        // If network renderer is active, skip local audio routing
        // The network device is handling playback, not local clients
        if (networkRendererActive) {
          console.log(`[ClientTransport] 🔇 Skipping local audio - ${activeRendererName} is playing`);
          break;
        }
        
        // NOTE: Do NOT send commands back to native in response to events!
        // Events are notifications that state has already changed.
        // Sending commands back creates an infinite loop.
        
        // WebAudioRenderer handles audio.play()/pause() via playbackStateCallbacks above.
        // Do NOT also manipulate this.audioElement here — that caused double-play "burps."
        
        console.log(`[ClientTransport] 🎵 State updated to: ${newState}`);
        break;
      }
      
      case 'trackChanged':
        if ('track' in payload) {
          this.trackCallbacks.forEach((cb) => cb(payload.track as TrackMetadata | null));
        }
        break;
      
      case 'handlerReady':
        console.log('[ClientTransport] Handler ready:', payload);
        this.isHandlerReady = true;
        break;
      
      case 'handlerError':
        console.error('[ClientTransport] Handler error:', payload);
        break;
    }
  }
  
  // ---------------------------------------------------------------------------
  // EVENT SUBSCRIPTIONS (with duplicate detection)
  // ---------------------------------------------------------------------------
  
  private subscriptionCounter = 0;
  
  /**
   * Helper to log subscription count and detect duplicates
   */
  private logSubscription(eventType: string, count: number, action: 'add' | 'remove'): void {
    const emoji = action === 'add' ? '📝' : '🗑️';
    console.log(`${emoji} [Transport] ${eventType} subscription ${action}ed (total: ${count})`);
    
    if (action === 'add' && count > 3) {
      console.warn(`⚠️ [Transport] High ${eventType} subscription count: ${count}`);
      console.warn('⚠️ [Transport] This may indicate duplicate subscriptions!');
    }
  }
  
  /**
   * Subscribe to playback state changes
   */
  onPlaybackStateChanged(callback: PlaybackStateCallback): () => void {
    this.playbackStateCallbacks.add(callback);
    this.logSubscription('playbackState', this.playbackStateCallbacks.size, 'add');
    
    return () => {
      this.playbackStateCallbacks.delete(callback);
      this.logSubscription('playbackState', this.playbackStateCallbacks.size, 'remove');
    };
  }
  
  /**
   * Subscribe to position updates
   */
  onPositionChanged(callback: PositionCallback): () => void {
    this.positionCallbacks.add(callback);
    this.logSubscription('position', this.positionCallbacks.size, 'add');
    
    return () => {
      this.positionCallbacks.delete(callback);
      this.logSubscription('position', this.positionCallbacks.size, 'remove');
    };
  }
  
  /**
   * Subscribe to track changes
   */
  onTrackChanged(callback: TrackCallback): () => void {
    this.trackCallbacks.add(callback);
    this.logSubscription('track', this.trackCallbacks.size, 'add');
    
    return () => {
      this.trackCallbacks.delete(callback);
      this.logSubscription('track', this.trackCallbacks.size, 'remove');
    };
  }
  
  /**
   * Subscribe to volume changes
   */
  onVolumeChanged(callback: VolumeCallback): () => void {
    this.volumeCallbacks.add(callback);
    this.logSubscription('volume', this.volumeCallbacks.size, 'add');
    
    return () => {
      this.volumeCallbacks.delete(callback);
      this.logSubscription('volume', this.volumeCallbacks.size, 'remove');
    };
  }
  
  /**
   * Subscribe to connection state changes
   */
  onConnectionChanged(callback: ConnectionCallback): () => void {
    this.connectionCallbacks.add(callback);
    this.logSubscription('connection', this.connectionCallbacks.size, 'add');
    
    return () => {
      this.connectionCallbacks.delete(callback);
      this.logSubscription('connection', this.connectionCallbacks.size, 'remove');
    };
  }
  
  /**
   * Subscribe to all events (generic listener)
   */
  onEvent(callback: EventCallback): () => void {
    this.genericEventCallbacks.add(callback);
    this.logSubscription('generic', this.genericEventCallbacks.size, 'add');
    
    return () => {
      this.genericEventCallbacks.delete(callback);
      this.logSubscription('generic', this.genericEventCallbacks.size, 'remove');
    };
  }
  
  /**
   * Debug: Get current subscription counts
   */
  getSubscriptionCounts(): Record<string, number> {
    return {
      playbackState: this.playbackStateCallbacks.size,
      position: this.positionCallbacks.size,
      track: this.trackCallbacks.size,
      volume: this.volumeCallbacks.size,
      connection: this.connectionCallbacks.size,
      generic: this.genericEventCallbacks.size,
    };
  }
  
  // ---------------------------------------------------------------------------
  // CONVENIENCE METHODS
  // ---------------------------------------------------------------------------
  
  /**
   * Play a track
   */
  /**
   * Start/Resume playback
   * 
   * CRITICAL: DUAL ROUTING for multi-device sync
   * 1. Native Bridge (if available) - For local iOS hardware feedback
   * 2. WebSocket Server (ALWAYS) - Cloud is the source of truth
   * 
   * NO EARLY RETURNS after native send!
   */
  async play(options?: {
    trackId?: string;
    streamUrl?: string;
    title?: string;
    artist?: string;
    album?: string;
    artworkUrl?: string;
    duration?: number;
    position?: number;
  }): Promise<BatonMessage<ResponsePayload>> {
    const startTime = Date.now();
    
    // If no track options provided, get current track from store to ensure server sync
    // This prevents "bare" play commands from losing track state
    let trackOptions = options;
    if (!trackOptions?.trackId && !trackOptions?.streamUrl) {
      const store = useMaestroStore.getState();
      const currentTrack = store.currentTrack;
      if (currentTrack) {
        trackOptions = {
          trackId: currentTrack.id,
          streamUrl: (currentTrack as any).streamUrl,
          title: currentTrack.title,
          artist: currentTrack.artist,
          album: (currentTrack as any).album,
          artworkUrl: currentTrack.artworkUrl,
          duration: currentTrack.duration,
          // CRITICAL: Include current position to prevent server reset to 0!
          // Without this, resuming after seek snaps playback back to start
          position: store.position,
          ...options,
        };
      }
    }

    // Ensure streamUrl exists for browser playback. Only use /stream/{id} for local/test media.
    // Qobuz tracks should already have a streamUrl from the stream-url API endpoint.
    const isQobuzSource = String((trackOptions as any)?.source || '').toLowerCase() === 'qobuz' ||
      !!(trackOptions as any)?._qobuzSource;
    if (trackOptions?.trackId && !trackOptions?.streamUrl && !isQobuzSource) {
      trackOptions = {
        ...trackOptions,
        streamUrl: `${CLOUD_API_URL}/stream/${encodeURIComponent(String(trackOptions.trackId))}`,
      };
    }
    
    // -------------------------------------------------------------------------
    // 0. CONNECTOR NETWORK RENDERER — route resume through Tauri IPC
    // When the Connector owns a network renderer, a bare play() (resume) must
    // go through Tauri IPC to reach the UPnP/AirPlay device.
    // -------------------------------------------------------------------------
    if (this.isConnectorRendererActive() && !trackOptions?.streamUrl) {
      console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - Connector renderer active, resuming via Tauri IPC`);
      const storeState = useMaestroStore.getState();
      const rendererId = storeState.activeConnectorRenderer?.id;
      if (rendererId) {
        const hasTauri = typeof (window as any).__TAURI__?.core?.invoke === 'function';
        if (hasTauri) {
          try {
            await (window as any).__TAURI__.core.invoke('play', { rendererId });
            console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - Connector play sent to ${rendererId}`);
            storeState.setPlaybackState('playing');
          } catch (err: any) {
            console.error('[ClientTransport] Connector play failed:', err);
          }
        }
      }
      const message = createCommand('transport', { cmd: 'play' as const, ...trackOptions } as CommandPayload);
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(message));
      }
      return this.createLocalResponse(message.id);
    }

    // Create BATON command envelope
    const payload = { cmd: 'play' as const, ...trackOptions };
    const message = createCommand('transport', payload as CommandPayload);
    const json = JSON.stringify(message);
    
    // -------------------------------------------------------------------------
    // 1. NATIVE BRIDGE (iOS / Connector network zones) - Send to native
    // -------------------------------------------------------------------------
    const iosBridge = this.getIOSBridge();
    const connectorLocal = this.isConnectorLocalZone();
    
    // Skip bridge when Connector is active with local zone — WebAudioRenderer handles it
    if (iosBridge && !connectorLocal) {
      // Stop local audio — a network renderer will handle playback
      if (this.audioElement && !this.audioElement.paused) {
        try {
          this.audioElement.pause();
          console.log(`📡 [ClientTransport] Local audio paused — network zone handles playback`);
        } catch { /* ignore */ }
      }
      console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - Sending PLAY to Native Bridge`);
      iosBridge.postMessage(json);
      console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - Native Bridge postMessage done`);
      // DO NOT RETURN - Continue to send to Cloud!
    }
    
    // -------------------------------------------------------------------------
    // 2. BROWSER (HTML5 Audio) - If Browser IS the output device
    // -------------------------------------------------------------------------
    // Connector local zone: bridge exists but audio plays locally through WebAudioRenderer
    const store = useMaestroStore.getState();
    const zoneName = store.system?.zoneName || '';
    const isBrowserOutputDevice = (!iosBridge || connectorLocal) && (
      zoneName === 'Desktop' ||
      zoneName === 'This Computer' ||
      zoneName === 'This Device' ||
      zoneName === 'System Audio' ||
      zoneName === '' ||
      zoneName.toLowerCase().includes('browser') ||
      zoneName.toLowerCase().includes('this computer') ||
      zoneName.toLowerCase().includes('this device') ||
      zoneName.toLowerCase().includes('system audio')
    );
    
    if (isBrowserOutputDevice && this.audioElement) {
      // Pre-seed src before attempting play.
      const streamUrl = String((trackOptions as any)?.streamUrl || '');
      if (streamUrl) {
        try {
          if (!this.audioElement.src || this.audioElement.src !== streamUrl) {
            this.audioElement.src = streamUrl;
            this.audioElement.load();
          }
        } catch {
          // ignore
        }

        // Emit local trackChanged for WebAudioRenderer determinism.
        const track = {
          id: (trackOptions as any)?.trackId || (store.currentTrack as any)?.id || 'local_stream',
          title: (trackOptions as any)?.title || (store.currentTrack as any)?.title || 'Unknown',
          artist: (trackOptions as any)?.artist || (store.currentTrack as any)?.artist || '',
          album: (trackOptions as any)?.album || (store.currentTrack as any)?.album || '',
          artworkUrl: (trackOptions as any)?.artworkUrl || (store.currentTrack as any)?.artworkUrl || '',
          duration: Number((trackOptions as any)?.duration || (store.currentTrack as any)?.duration || 0) || 0,
          source: 'local' as any,
          streamUrl,
        } as unknown as TrackMetadata;

        try {
          store.setCurrentTrack(track as any);
        } catch {
          // ignore
        }
        this.trackCallbacks.forEach((cb) => cb(track));
      }

      this.audioElement.play().catch((err) => {
        console.error(`[ClientTransport] Browser audio.play() failed:`, err.message);
      });
    }
    
    // -------------------------------------------------------------------------
    // 3. WEBSOCKET (Cloud Sync) - ALWAYS EXECUTE
    // -------------------------------------------------------------------------
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - Sending PLAY to WebSocket server`);
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.log(`📡 [ClientTransport] PLAY command TIMEOUT after ${this.commandTimeout}ms`);
          this.pendingCommands.delete(message.id);
          reject(new Error('Command timeout: play'));
        }, this.commandTimeout);
        
        this.pendingCommands.set(message.id, { 
          resolve: (response) => {
            console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - PLAY response received`);
            resolve(response);
          }, 
          reject, 
          timeout 
        });
        this.ws!.send(json);
        console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - PLAY sent to WebSocket`);
      });
    } else {
      console.warn('[ClientTransport] ⚠️ WebSocket not connected - play not synced to server');
      
      // If native bridge handled it, return a mock success response
      if (iosBridge) {
        return {
          v: PROTOCOL_VERSION,
          id: message.id,
          ts: Date.now(),
          type: 'response',
          handler: 'transport',
          location: 'local',
          payload: { success: true, cmd: 'play' },
        } as unknown as BatonMessage<ResponsePayload>;
      }
      
      // Browser played locally even if server not connected
      if (this.audioElement) {
        return {
          v: PROTOCOL_VERSION,
          id: message.id,
          ts: Date.now(),
          type: 'response',
          handler: 'transport',
          location: 'local',
          payload: { success: true, cmd: 'play' },
        } as unknown as BatonMessage<ResponsePayload>;
      }
      
      throw new Error('Not connected to server');
    }
  }
  
  /**
   * Pause playback
   * 
   * CRITICAL: DUAL ROUTING for multi-device sync
   * 1. Native Bridge (if available) - For local iOS hardware feedback
   * 2. Browser Audio Element - Pause IMMEDIATELY (user gesture context)
   * 3. WebSocket Server (ALWAYS) - Cloud is the source of truth
   * 
   * NO EARLY RETURNS after native send!
   */
  async pause(): Promise<BatonMessage<ResponsePayload>> {
    const startTime = Date.now();
    console.log(`📡 [ClientTransport] pause() called`);
    
    // Create BATON command envelope
    const payload = { cmd: 'pause' as const };
    const message = createCommand('transport', payload as CommandPayload);
    const json = JSON.stringify(message);
    
    // -------------------------------------------------------------------------
    // 1. CONNECTOR NETWORK RENDERER — route through Tauri IPC
    // When the Connector owns a network renderer, the pause must go through
    // Tauri IPC (invoke('pause')) to reach the UPnP/AirPlay device.
    // WebSocket and __MAESTRO_BRIDGE__ cannot control local UPnP renderers.
    // -------------------------------------------------------------------------
    if (this.isConnectorRendererActive()) {
      console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - Connector renderer active, pausing via Tauri IPC`);
      const store = useMaestroStore.getState();
      const rendererId = store.activeConnectorRenderer?.id;
      if (rendererId) {
        const hasTauri = typeof (window as any).__TAURI__?.core?.invoke === 'function';
        if (hasTauri) {
          try {
            await (window as any).__TAURI__.core.invoke('pause', { rendererId });
            console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - Connector pause sent to ${rendererId}`);
          } catch (err: any) {
            console.error('[ClientTransport] Connector pause failed:', err);
          }
        }
      }
      // Sync state to WebSocket for multi-device sync, but do NOT fire
      // playbackStateCallbacks (the Connector is the source of truth).
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(json);
      }
      return this.createLocalResponse(message.id);
    }
    
    // -------------------------------------------------------------------------
    // 2. NATIVE BRIDGE (iOS)
    // -------------------------------------------------------------------------
    const iosBridge = this.getIOSBridge();
    const connectorLocal = this.isConnectorLocalZone();
    
    if (iosBridge && !connectorLocal) {
      console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - Sending PAUSE to Native Bridge`);
      iosBridge.postMessage(json);
      // DO NOT RETURN - Continue to send to Cloud!
    }
    
    // -------------------------------------------------------------------------
    // 3. BROWSER (HTML5 Audio)
    // -------------------------------------------------------------------------
    const store = useMaestroStore.getState();
    const zoneName = store.system?.zoneName || '';
    const isBrowserOutputDevice = (!iosBridge || connectorLocal) && (
      zoneName === 'Desktop' ||
      zoneName === 'This Computer' ||
      zoneName === 'This Device' ||
      zoneName === 'System Audio' ||
      zoneName === '' ||
      zoneName.toLowerCase().includes('browser') ||
      zoneName.toLowerCase().includes('this computer') ||
      zoneName.toLowerCase().includes('this device') ||
      zoneName.toLowerCase().includes('system audio')
    );
    
    if (isBrowserOutputDevice && this.audioElement) {
      this.audioElement.pause();
    }
    
    // -------------------------------------------------------------------------
    // 4. WEBSOCKET (Cloud Sync) - ALWAYS EXECUTE
    // -------------------------------------------------------------------------
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - Sending PAUSE to WebSocket server`);
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.log(`📡 [ClientTransport] PAUSE command TIMEOUT after ${this.commandTimeout}ms`);
          this.pendingCommands.delete(message.id);
          reject(new Error('Command timeout: pause'));
        }, this.commandTimeout);
        
        this.pendingCommands.set(message.id, { 
          resolve: (response) => {
            console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - PAUSE response received`);
            resolve(response);
          },
          reject, 
          timeout 
        });
        this.ws!.send(json);
        console.log(`📡 [ClientTransport] ${Date.now() - startTime}ms - PAUSE sent to WebSocket`);
      });
    } else {
      console.warn('[ClientTransport] ⚠️ WebSocket not connected - pause not synced to server');
      
      // If native bridge handled it, return a mock success response
      if (iosBridge) {
        return {
          v: PROTOCOL_VERSION,
          id: message.id,
          ts: Date.now(),
          type: 'response',
          handler: 'transport',
          location: 'local',
          payload: { success: true, cmd: 'pause' },
        } as unknown as BatonMessage<ResponsePayload>;
      }
      
      // Browser paused locally even if server not connected
      if (this.audioElement) {
        return {
          v: PROTOCOL_VERSION,
          id: message.id,
          ts: Date.now(),
          type: 'response',
          handler: 'transport',
          location: 'local',
          payload: { success: true, cmd: 'pause' },
        } as unknown as BatonMessage<ResponsePayload>;
      }
      
      throw new Error('Not connected to server');
    }
  }
  
  /**
   * Toggle play/pause based on current state
   * @param isCurrentlyPlaying - Current playback state (true = playing, false = paused/stopped)
   * @returns Promise resolving to the command response
   * 
   * Logic: Sends the INVERSE command
   * - If currently playing → send 'pause'
   * - If currently paused/stopped → send 'play'
   */
  async togglePlay(isCurrentlyPlaying: boolean): Promise<BatonMessage<ResponsePayload>> {
    if (isCurrentlyPlaying) {
      console.log('[ClientTransport] togglePlay: Currently playing → sending pause');
      return this.pause();
    } else {
      console.log('[ClientTransport] togglePlay: Currently paused → sending play');
      return this.play();
    }
  }
  
  /**
   * Stop playback
   */
  async stop(): Promise<BatonMessage<ResponsePayload>> {
    return this.sendCommand({ cmd: 'stop' });
  }
  
  /**
   * Skip to next track
   */
  async next(): Promise<BatonMessage<ResponsePayload>> {
    return this.sendCommand({ cmd: 'next' });
  }
  
  /**
   * Go to previous track
   */
  async previous(): Promise<BatonMessage<ResponsePayload>> {
    return this.sendCommand({ cmd: 'previous' });
  }
  
  /**
   * Seek to position (in seconds)
   * 
   * BATON Protocol: Seeks playback to the specified position.
   * 
   * DUAL ROUTING (like play/pause):
   * 1. iOS Native Bridge - For local AVPlayer seek
   * 2. WebSocket (Cloud) - For multi-device sync
   * 
   * CRITICAL: Must send to native bridge when iPhone is playing locally!
   */
  async seek(position: number): Promise<BatonMessage<ResponsePayload>> {
    // 1. SANITIZE: Ensure position is a positive number
    const safePosition = Math.max(0, Math.floor(position));
    
    // Create BATON command envelope
    const payload = { cmd: 'seek' as const, position: safePosition };
    const message = createCommand('transport', payload as CommandPayload);
    const json = JSON.stringify(message);
    
    // -------------------------------------------------------------------------
    // 2. NATIVE BRIDGE - Send for network zones (skip for Connector local)
    // -------------------------------------------------------------------------
    const iosBridge = this.getIOSBridge();
    const connectorLocal = this.isConnectorLocalZone();
    
    if (iosBridge && !connectorLocal) {
      console.log(`[ClientTransport] 📱 Sending SEEK to native: ${safePosition}s`);
      iosBridge.postMessage(json);
      // DO NOT RETURN - Continue to send to Cloud for sync!
    }
    
    // -------------------------------------------------------------------------
    // 3. BROWSER: Apply seek to local audio element immediately
    // -------------------------------------------------------------------------
    if ((!iosBridge || connectorLocal) && this.audioElement) {
      console.log(`[ClientTransport] 🎵 Browser: Seeking audio element to ${safePosition}s`);
      try {
        this.audioElement.currentTime = safePosition;
      } catch (err) {
        console.error('[ClientTransport] ❌ Audio seek failed:', err);
      }
    }
    
    // -------------------------------------------------------------------------
    // 4. WEBSOCKET (Cloud Sync) - Send to server for multi-device sync
    // -------------------------------------------------------------------------
    console.log(`[ClientTransport] ⏩ Sending SEEK command to Cloud: ${safePosition}s`);
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.pendingCommands.delete(message.id);
          reject(new Error('Command timeout: seek'));
        }, this.commandTimeout);
        
        this.pendingCommands.set(message.id, { resolve, reject, timeout });
        this.ws!.send(json);
        
        console.log(`[ClientTransport] 📤 SEEK sent to server: position=${safePosition}s`);
      });
    } else {
      // WebSocket not connected - this is a problem for iPhone!
      if (this.hasNativeBridge() && !connectorLocal) {
        console.error('[ClientTransport] ❌ WebSocket not connected - iPhone seek will NOT reach Browser!');
        throw new Error('Not connected to server - seek cannot be sent');
      }
      
      // Browser can still work locally even if not connected
      console.warn('[ClientTransport] ⚠️ WebSocket not connected - seek applied locally only');
      return {
        v: PROTOCOL_VERSION,
        id: message.id,
        ts: Date.now(),
        type: 'response',
        handler: 'transport',
        location: 'local',
        payload: { success: true, cmd: 'seek' },
      } as unknown as BatonMessage<ResponsePayload>;
    }
  }
  
  /**
   * Set volume (0-100)
   * @param level - Volume level as integer between 0-100
   * 
   * CRITICAL FIX: This method MUST send to BOTH:
   * 1. Native Bridge (if available) - For local iOS hardware feedback
   * 2. WebSocket Server (ALWAYS) - For multi-device sync
   * 
   * NO EARLY RETURNS after native send!
   */
  async setVolume(level: number): Promise<BatonMessage<ResponsePayload>> {
    // 1. SANITIZE: Floor to integer, clamp to valid range (0-100)
    const safeLevel = Math.max(0, Math.min(100, Math.floor(level)));
    console.log(`[ClientTransport] setVolume called: raw=${level}, safe=${safeLevel}`);

    // -------------------------------------------------------------------------
    // LOCAL APPLY (Browser playback)
    // Cloud/bridge commonly does NOT echo volumeChanged back to sender to avoid UI jitter.
    // For local browser playback, we must apply volume immediately to the audio element.
    // -------------------------------------------------------------------------
    try {
      const store = useMaestroStore.getState();
      store.setVolume(safeLevel);
      store.setMuted(false);
    } catch {
      // ignore
    }
    this.applyVolumeToAudioElement(safeLevel);
    this.volumeCallbacks.forEach((cb) => cb(safeLevel, false));
    
    // Create BATON command envelope
    const payload = { cmd: 'setVolume' as const, level: safeLevel };
    const message = createCommand('transport', payload as CommandPayload);
    const json = JSON.stringify(message);
    
    // Volume commands to native renderers (iOS/Tauri) are handled exclusively
    // by TransportCommand.volume(). Do NOT duplicate via the iOS bridge here.
    
    // -------------------------------------------------------------------------
    // 3. WEBSOCKET SERVER (Cloud Sync) - MUST EXECUTE ALWAYS
    // -------------------------------------------------------------------------
    console.log('[ClientTransport] 📤 Sending volume command to Cloud:', safeLevel);
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(json);
      console.log('[ClientTransport] ✅ Volume command sent to server');
    } else {
      console.error('[ClientTransport] ❌ WebSocket NOT connected! Volume NOT synced to server!');
      console.error('[ClientTransport] WebSocket state:', this.ws?.readyState, 'Expected:', WebSocket.OPEN);
    }
    
    // Return a promise that resolves when server responds (or immediately if no WS)
    return new Promise((resolve, reject) => {
      // If we have WebSocket, wait for server response
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const timeout = setTimeout(() => {
          this.pendingCommands.delete(message.id);
          // Don't reject on timeout for volume - it's not critical
          console.warn(`[ClientTransport] Volume command timeout (server may not respond)`);
          try {
            const st = useMaestroStore.getState();
            const rn = st.discovery?.activeZone?.name || st.activeConnectorRenderer?.name || 'session';
            diagnosticLog(
              'warn',
              'volume',
              `Volume response from ${rn}: timeout`,
              { renderer: rn, requestedLevel: safeLevel, reportedLevel: undefined },
            );
          } catch { /* ignore */ }
          resolve({
            v: PROTOCOL_VERSION,
            id: message.id,
            ts: Date.now(),
            type: 'response',
            handler: 'transport',
            payload: { success: true, local: true },
          } as BatonMessage<ResponsePayload>);
        }, this.commandTimeout);
        
        this.pendingCommands.set(message.id, { resolve, reject, timeout });
      } else {
        // No WebSocket - resolve immediately (native-only mode)
        resolve({
          v: PROTOCOL_VERSION,
          id: message.id,
          ts: Date.now(),
          type: 'response',
          handler: 'transport',
          payload: { success: true, local: true },
        } as BatonMessage<ResponsePayload>);
      }
    }).then((msg) => {
      try {
        const st = useMaestroStore.getState();
        const rn = st.discovery?.activeZone?.name || st.activeConnectorRenderer?.name || 'session';
        diagnosticLog(
          'info',
          'volume',
          `Volume response from ${rn}: ok`,
          { renderer: rn, requestedLevel: safeLevel, reportedLevel: st.volume },
        );
      } catch { /* ignore */ }
      return msg;
    });
  }
  
  /**
   * Mute audio
   * DUAL ROUTING: Native Bridge (feedback) AND Server (sync)
   */
  async mute(): Promise<BatonMessage<ResponsePayload>> {
    console.log(`[ClientTransport] mute command`);
    
    const payload = { cmd: 'mute' as const };
    const message = createCommand('transport', payload as CommandPayload);
    const json = JSON.stringify(message);
    
    // Native Bridge (if available)
    const iosBridge = this.getIOSBridge();
    if (iosBridge) {
      iosBridge.postMessage(json);
      console.log(`[ClientTransport] 📲 Mute sent to Native Bridge`);
    }
    
    // WebSocket Server (ALWAYS for sync)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(json);
      console.log(`[ClientTransport] 📤 Sending mute command to Cloud`);
    }
    
    return this.createLocalResponse(message.id);
  }
  
  /**
   * Unmute audio
   * DUAL ROUTING: Native Bridge (feedback) AND Server (sync)
   */
  async unmute(): Promise<BatonMessage<ResponsePayload>> {
    console.log(`[ClientTransport] unmute command`);
    
    const payload = { cmd: 'unmute' as const };
    const message = createCommand('transport', payload as CommandPayload);
    const json = JSON.stringify(message);
    
    // Native Bridge (if available)
    const iosBridge = this.getIOSBridge();
    if (iosBridge) {
      iosBridge.postMessage(json);
      console.log(`[ClientTransport] 📲 Unmute sent to Native Bridge`);
    }
    
    // WebSocket Server (ALWAYS for sync)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(json);
      console.log(`[ClientTransport] 📤 Sending unmute command to Cloud`);
    }
    
    return this.createLocalResponse(message.id);
  }
  
  /**
   * Create a local success response (for fire-and-forget commands)
   */
  private createLocalResponse(messageId: string): Promise<BatonMessage<ResponsePayload>> {
    return Promise.resolve({
      v: PROTOCOL_VERSION,
      id: messageId,
      ts: Date.now(),
      type: 'response',
      handler: 'transport',
      payload: { success: true, local: true },
    } as BatonMessage<ResponsePayload>);
  }
  
  /**
   * Get current status
   */
  async getStatus(): Promise<BatonMessage<ResponsePayload>> {
    return this.sendCommand({ cmd: 'status' });
  }
  
  // ---------------------------------------------------------------------------
  // BROWSER AS BATON DEVICE: Audio Element & Position Broadcasting
  // ---------------------------------------------------------------------------
  
  /**
   * Register the HTML5 audio element reference
   * This allows the handler to:
   * 1. Apply remote volume commands to the audio element
   * 2. Broadcast position updates to the server
   */
  registerAudioElement(audio: HTMLAudioElement): void {
    this.audioElement = audio;
    console.log('[ClientTransport] 🔊 Audio element registered for BATON device mode');
  }
  
  /**
   * Unregister the audio element (cleanup)
   */
  unregisterAudioElement(): void {
    this.stopPositionBroadcast();
    this.audioElement = null;
    console.log('[ClientTransport] 🔊 Audio element unregistered');
  }

  /**
   * Local-first playback (desktop browser)
   *
   * This is used when the browser itself is the playback target (e.g., "This Computer"),
   * so we don’t depend on a bridge/server to emit trackChanged before audio starts.
   */
  async playLocalStream(track: TrackMetadata & { streamUrl: string }, opts?: { sharedFallback?: boolean }): Promise<void> { // #10426-a1 sharedFallback: exclusive→shared on local-native — play locally, never renderer-gated
    if ((window as any).__TAURI__) {
      const { ensureConnectorInfo } = await import('../lib/connector');
      await ensureConnectorInfo();
    }
    const { resolveLocalRendererStreamUrl } = await import('../lib/playback-bridge');
    const streamUrl = resolveLocalRendererStreamUrl(String(track.streamUrl || ''), {
      context: 'ClientTransportHandler.playLocalStream',
    });
    const trackForLocal = streamUrl !== track.streamUrl
      ? { ...track, streamUrl }
      : track;

    const store = useMaestroStore.getState();
    const sharedLocal = !!opts?.sharedFallback || shouldRouteStickyShared(store.discovery?.activeZone as any, trackForLocal); // #10426-a1: recovery paths play under sticky shared
    if (this.isConnectorRendererActive() && !sharedLocal) {
      if (store.playbackState === 'paused') {
        console.log('[ClientTransport] playLocalStream blocked — renderer is paused');
        return;
      }
      const incomingId = (trackForLocal as any)?.id || (trackForLocal as any)?.trackId;
      if (incomingId && incomingId === this._lastBridgePlayedTrackId) {
        console.log('[ClientTransport] playLocalStream blocked — same track already on renderer');
        return;
      }
      this._lastBridgePlayedTrackId = incomingId;
      console.log('[ClientTransport] playLocalStream allowed — new track:', (trackForLocal as any)?.title);
    }

    // Update store so UI reflects immediately
    store.setCurrentTrack(trackForLocal);
    if (typeof trackForLocal.duration === 'number') {
      store.setDuration(trackForLocal.duration);
    }

    // When a native app (Connector or iOS) is active and a network zone is selected,
    // route the play command through the native bridge, which sends UPnP SOAP or
    // RAAT commands to the renderer. Local audio does NOT play.
    const p = detectPlatform();
    const isConnector = p.isTauriDesktop || !!(window as any).__MAESTRO_CONNECTOR__;
    const hasNativeBridge = p.hasNativeBridge;
    const activeZone = store.discovery?.activeZone;
    const isLocalId = !activeZone ||
      activeZone.id === 'this_computer' ||
      activeZone.id === 'local_computer' ||
      activeZone.id === 'this_device';
    const isLocalType = (activeZone as any)?.zone_type === 'browser' ||
      (activeZone as any)?.type === 'browser' ||
      (activeZone as any)?.type === 'local' || (sharedLocal && (activeZone as any)?.type === 'local-native');
    const isNetworkZone = hasNativeBridge && !isLocalId && !isLocalType;

    if (isNetworkZone) {
      // Network zone playback is handled exclusively by TransportCommand.playUri().
      // Do NOT send commands from here — it causes dual-play on iOS and stale
      // setActiveConnectorRenderer calls that reset rendererHasStream.
      console.log('[ClientTransport] playLocalStream skipped — network zone active, TransportCommand owns this path');
      return;
    }

    // Local playback — emit to WebAudioRenderer
    this.trackCallbacks.forEach((cb) => cb(trackForLocal));

    // Emit playing state — WebAudioRenderer handles audio.play()
    store.setPlaybackState('playing');
    this.playbackStateCallbacks.forEach((cb) => cb('playing'));
    clearExclusiveErrorOnPlaybackConfirmed();
  }
  
  // ---------------------------------------------------------------------------
  // LOCAL PLAYBACK PREP HELPER
  // ---------------------------------------------------------------------------

  /**
   * If this is a local-zone play command, set audio src and emit trackChanged
   * so WebAudioRenderer handles playback. On the Connector, the bridge exists
   * but local playback still goes through WebAudioRenderer when the active zone
   * is "This Computer".
   */
  private maybeDoLocalPlaybackPrep(
    handler: HandlerId,
    payload: CommandPayload,
    iosBridge: any
  ): void {
    if (handler !== 'transport' || (payload as any)?.cmd !== 'play') return;

    const store = useMaestroStore.getState();
    const zn = String(store.system?.zoneName || '');
    const isConnector = !!(window as any).__MAESTRO_CONNECTOR__;

    const connectorLocalZone = isConnector && (() => {
      const az = store.discovery?.activeZone;
      if (!az) return true;
      const id = az.id || '';
      if (id === 'this_computer' || id === 'local_computer' || id === 'this_device') return true;
      const t = (az as any).type || '';
      return t === 'local' || t === 'browser';
    })();

    const isBrowserOutput =
      (!iosBridge || connectorLocalZone) &&
      (
        zn === '' ||
        zn === 'Desktop' ||
        zn === 'This Computer' ||
        zn === 'This Device' ||
        zn === 'System Audio' ||
        zn.toLowerCase().includes('browser') ||
        zn.toLowerCase().includes('this computer') ||
        zn.toLowerCase().includes('this device') ||
        zn.toLowerCase().includes('system audio')
      );

    const streamUrl = String((payload as any)?.streamUrl || '');
    if (!isBrowserOutput || !streamUrl) return;

    if (this.audioElement) {
      try {
        if (!this.audioElement.src || this.audioElement.src !== streamUrl) {
          this.audioElement.src = streamUrl;
          this.audioElement.load();
        }
      } catch { /* ignore */ }
    }

    const current = store.currentTrack as any;
    const track = {
      id: (payload as any)?.trackId || current?.id || 'local_stream',
      title: (payload as any)?.title || current?.title || 'Unknown',
      artist: (payload as any)?.artist || current?.artist || '',
      album: (payload as any)?.album || current?.album || '',
      artworkUrl: (payload as any)?.artworkUrl || current?.artworkUrl || '',
      duration: Number((payload as any)?.duration || current?.duration || 0) || 0,
      source: 'local' as any,
      streamUrl,
    } as unknown as TrackMetadata;

    try { store.setCurrentTrack(track as any); } catch { /* ignore */ }
    this.trackCallbacks.forEach((cb) => cb(track));
  }

  // ---------------------------------------------------------------------------
  // POSITION LOCK CONTROL
  // ---------------------------------------------------------------------------
  
  /**
   * Lock position updates (called by App.jsx when user starts scrubbing)
   * While locked, server position events are ignored to prevent "snap back"
   */
  lockPosition(): void {
    this.positionLocked = true;
    console.log('[ClientTransport] 🔒 Position locked (user scrubbing)');
  }
  
  /**
   * Unlock position updates (called by App.jsx when user stops scrubbing)
   */
  unlockPosition(): void {
    this.positionLocked = false;
    console.log('[ClientTransport] 🔓 Position unlocked (accepting server updates)');
  }
  
  /**
   * Check if position is currently locked
   */
  isPositionLocked(): boolean {
    return this.positionLocked;
  }
  
  /**
   * Start broadcasting position updates to the server every 1 second
   * This allows remote devices (iPhone) to see the progress bar move
   */
  startPositionBroadcast(): void {
    // Don't start if already running or no audio element
    if (this.positionBroadcastInterval || !this.audioElement) {
      return;
    }
    
    console.log('[ClientTransport] 📡 Starting position broadcast (1s interval)');
    
    this.positionBroadcastInterval = setInterval(() => {
      if (!this.audioElement || this.audioElement.paused) {
        return;
      }
      
      const currentSecs = Math.floor(this.audioElement.currentTime);
      const totalSecs = Math.floor(this.audioElement.duration) || 0;
      
      // Throttle: only broadcast if position actually changed
      if (currentSecs === this.lastBroadcastPosition) {
        return;
      }
      
      this.lastBroadcastPosition = currentSecs;
      
      // Update local store directly — MaestroRoom uses broadcastToOthers,
      // so position events never echo back to the sender.
      useMaestroStore.getState().updateTime(currentSecs, totalSecs);
      
      // Send positionChanged event to server (for remote devices to sync)
      this.broadcastPositionEvent(currentSecs, totalSecs);
    }, 1000);
  }
  
  /**
   * Stop broadcasting position updates
   */
  stopPositionBroadcast(): void {
    if (this.positionBroadcastInterval) {
      clearInterval(this.positionBroadcastInterval);
      this.positionBroadcastInterval = null;
      this.lastBroadcastPosition = -1;
      console.log('[ClientTransport] 📡 Position broadcast stopped');
    }
  }
  
  /**
   * Broadcast a positionChanged event to the server
   * This is sent as a BATON event so remote devices can sync their UI
   */
  private broadcastPositionEvent(position: number, duration: number): void {
    const event = {
      v: PROTOCOL_VERSION,
      id: `pos_${Date.now()}`,
      ts: Date.now(),
      type: 'event' as const,
      handler: 'transport',
      location: 'local' as HandlerLocation,
      payload: {
        event: 'positionChanged',
        position,
        duration,
      },
    };
    
    const json = JSON.stringify(event);
    
    // Send via WebSocket to server (server will relay to other clients)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(json);
      // Only log occasionally to avoid spam
      if (position % 10 === 0) {
        console.log(`[ClientTransport] 📡 Position broadcast: ${position}s / ${duration}s`);
      }
    }
  }
  
  /**
   * Apply volume to the registered audio element
   * Called when receiving remote setVolume commands
   */
  private applyVolumeToAudioElement(level: number): void {
    if (this.audioElement) {
      // Convert 0-100 to 0.0-1.0
      const normalizedVolume = Math.max(0, Math.min(1, level / 100));
      this.audioElement.volume = normalizedVolume;
      console.log(`[ClientTransport] 🔊 Applied volume to audio element: ${level}% (${normalizedVolume})`);
    }
  }
  
  // ---------------------------------------------------------------------------
  // ACCESSORS
  // ---------------------------------------------------------------------------
  
  /**
   * Get current connection state
   */
  getConnectionState(): ClientConnectionState {
    return this.connectionState;
  }
  
  /**
   * Check if connected and handler is ready
   */
  isReady(): boolean {
    return this.connectionState === 'connected' && this.isHandlerReady;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === 'connected';
  }
  
  /**
   * Get current transport mode
   */
  getTransportMode(): TransportMode {
    return this.transportMode;
  }
  
  /**
   * Check if using native iOS bridge
   */
  isNative(): boolean {
    return this.transportMode === 'native';
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

let clientTransportInstance: ClientTransportHandler | null = null;

/**
 * Get the singleton ClientTransportHandler instance
 */
export function getClientTransport(): ClientTransportHandler {
  if (!clientTransportInstance) {
    clientTransportInstance = new ClientTransportHandler();
  }
  return clientTransportInstance;
}

/**
 * Reset the singleton (for testing)
 */
export function resetClientTransport(): void {
  if (clientTransportInstance) {
    clientTransportInstance.disconnect();
    clientTransportInstance = null;
  }
}

